import ToggleSwitch from "components/common/NewSwitch";
import SelectOption from "components/common/Select/Select";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Accordion from "./BreakdownAccordion";
import {
  calculateDuration,
  calculateDurationStartAndEndTime,
  calculateTotalDuration,
  convertTimeToDatetimeLocal,
  convertTimeToDatetimeLocalWithoutMoment,
  customFormatDate,
  extractTimeFromDateTime,
  getShiftLength,
  isCheckNextDayShift,
  isEndTimeNextDay,
  parseBooleanVal,
} from "Utils/utils";
import { Controller, useForm } from "react-hook-form";
import NDISItem from "./NDISItem";
import { showClaimTypeInScheduleTimesheets } from "pages/Rostering/WorkerShift/components/advanceModeSplitDefaults";
import {
  URL_ROSTER_INVOICING_TIMESHEET_BREAKDOWN,
  URL_ROSTERING_INVOICING_GET_ITEM_NUMBER_ON_TIMESHEET,
  URL_ROSTERING_INVOICING_GET_TRAVEL_DETAILS,
  URL_ROSTERING_TIMESHEET_UPDATE_CODE_ALLOCATION,
  URL_ROSTERING_SHIFT_V5,
  URL_V5_SHIFT_BILLING_ALLOCATION,
  URL_V5_SHIFT_BILLING_BREAKDOWN_PREVIEW,
  URL_V5_SHIFT_SPLITS,
} from "store/services/URL";
import { apiGetRequest, apiPostRequest } from "helpers/Requests";
import { useDispatch, useSelector } from "react-redux";
import { getSpRosteringSetting } from "store/selector/sp.selector";
import { getServiceCatalogStates } from "store/selector/cos.selector";
import { getCatalogServicesSubCategoryRequest } from "store/actions/cos.actions";
import { CATEGORY_ITEM_NAME_AND_NOTES, USER_ROLE, getLineItemLabel } from "Constants/constant";
import { isEmpty } from "lodash";
import { syncTimesheetTableRowFromDetail } from "../../../utils/syncTimesheetTableRow";
import {
  resolveBasicShiftDurationHHMM,
  resolveInvoiceTimePreference,
  shiftClocksSpanNextCalendarDay,
} from "../../../utils/billingShiftRange";
import {
  calculateCodeAllocationInvoiceEstimate,
  formatCodeAllocationInvoiceEstimate,
} from "../../../utils/codeAllocationInvoiceEstimate";
import { resolveCodeAllocationRateStripValues } from "../../../utils/codeAllocationRates";
import {
  clearBreakdownFormFields,
  getBreakdownSegmentHydrationKey,
  getItemNumberOptionsSignature,
  isPostgresIntegerId,
  mapBreakdownItemNumbersToOptions,
  resolveSubmitBreakdownUnitRate,
} from "../../../utils/breakdownFormHydration";
import { TimesheetCodeAllocationTabSkeleton } from "../../../components/TimesheetRevamp/TimesheetDetailTabSkeletons";
import {
  TimesheetCodeAllocationBillingFlags,
  TimesheetCodeAllocationBillingInsight,
  TimesheetCodeAllocationBreakdownsHeader,
  TimesheetCodeAllocationBreakdownToggleRow,
  TimesheetCodeAllocationParticipantGroup,
  TimesheetCodeAllocationRateStrip,
  TimesheetCodeAllocationSettingsColumnHeader,
  TimesheetCodeAllocationShiftDurationPill,
  TimesheetCodeAllocationRatioBadge,
  TimesheetCodeAllocationSaveBar,
  TimesheetCodeAllocationSectionCard,
  TimesheetCodeAllocationShiftTimePendingBar,
  TimesheetCodeAllocationUpdateWarningModal,
  TimesheetCodeAllocationTogglePill,
} from "../../../components/TimesheetRevamp/TimesheetCodeAllocationLayout";
import { toast } from "react-toastify";
import InputField from "components/common/Input/Input";
import moment from "moment";
import { roleAccess } from "helpers/RolesPermission";
import { getUser } from "store/selector/auth.selector";
import { IoWarningOutline } from "react-icons/io5";
import { validateAdvanceShiftEndTime, getAdvanceSegmentWindowMinutes, sumAdvanceSegmentMinutes } from "pages/Rostering/WorkerShift/components/advanceModeValidation";
import { getBasicShiftDurationHHMM } from "pages/Rostering/WorkerShift/components/basicModeValidation";
import { TOAST_IDS } from "store/constants";
import "../../../components/TimesheetRevamp/timesheet-detail-revamp.css";
import TimesheetModeBanner from "../../components/TimesheetModeBanner";
import {
  buildSplitBreakdownRows,
  mergeBreakdownSavedLineItemOption,
  enrichInvoiceLinePayload,
  getBillingLines,
  getBillingSplitMeta,
  getBillingSplits,
  getDefaultInvoiceBreakdown,
  getShiftUuidForBilling,
  isBillingLocked,
  getBillingInvoiceNumber,
  parseShiftBillingDetailResponse,
  parseShiftBillingSaveResponse,
  parseShiftBreakdownPreviewResponse,
  isLegacySplitShift,
  isSplitBillingMode,
  isV5MultiAssignmentTimesheet,
  shouldShowServiceCategoryFields,
  shouldShowSplitBreakdownAccordion,
  groupBreakdownIndicesByParticipant,
  countUniqueBillingParticipants,
  getBreakdownParticipantKey,
  formatBillingParticipantNames,
  formatBillingDurationLabel,
  getParticipantInitials,
  hasMultiSegmentBillingSplits,
  shouldConfirmShiftTimeChange,
  normalizeClockTimeInput,
  buildShiftWindowDatetimes,
  buildAdvanceSegmentBillingDatetimes,
  collapseBreakdownRowsToShiftWindow,
  applyShiftWindowToShiftDetails,
  buildV5SplitsStorePayload,
  shouldUseScheduleWindowForBillingLine,
  shouldUseWorkerWindowForBillingLine,
  getEditorStateFromBillingSave,
  mergePreviewWithSavedBillingLines,
  savedBreakdownWindowsAreCollapsedDuplicates,
  syncBreakdownFormAfterBillingSave,
  validateAdvanceSleepoverPerParticipant,
  CODE_ALLOCATION_MAX_BREAKDOWN_SPLITS,
  shiftBreakdownFormFieldsAfterInsert,
  shiftBreakdownFormFieldsAfterRemove,
  copyBreakdownFormSegment,
  cloneBreakdownRowForSegmentSplit,
  applyLocalBreakdownRowsToShiftDetails,
  getParticipantTravelRows,
  hydrateParticipantTravelRows,
  buildParticipantTravelSavePayload,
  sortBillingRowsByStartTime,
} from "./v5BillingUtils";
import { clearNdisLineItemsCache } from "./ndisLineItemsFetchCache";
import V5ParticipantTravelPanel from "./V5ParticipantTravelPanel";

const SHIFT_TIME_CONFIRM_DELAY_MS = 1200;

/** validateAdvanceShiftEndTime returns `true` on success — normalize to null. */
const getShiftWindowValidationError = (startTime, endTime) => {
  const result = validateAdvanceShiftEndTime(startTime, endTime);
  if (result === true || result == null) return null;
  return result;
};

const resolveAccordionItemNumbers = (item, fallbackItemNumber = []) => {
  const mapped = mapBreakdownItemNumbersToOptions(item?.item_numbers?.items);
  if (mapped.length > 0) return mapped;
  return Array.isArray(fallbackItemNumber) ? fallbackItemNumber : [];
};

const getAccordionItemNumberKey = (item, idx) =>
  `${item?._clientKey ?? item?.id ?? item?.uuid ?? idx}|${
    item?.start_time ?? ""
  }|${item?.shift_type ?? ""}`;

const EMPTY_ITEM_NUMBERS = Object.freeze([]);

const resolveAccordionSuggestedItem = (item) => {
  const selected = item?.item_numbers?.selected;
  if (item?.custom_line_item_uuid || item?.custom_line_item?.uuid) {
    const uuid = item?.custom_line_item_uuid || item?.custom_line_item?.uuid;
    return {
      id: `custom:${uuid}`,
      price: item?.invoice_price ?? item?.ndis_rate ?? null,
      support_item_name: item?.ndis_support_item_name ?? null,
      support_item_number: item?.ndis_support_item_number ?? null,
    };
  }
  if (
    selected &&
    typeof selected === "object" &&
    selected.id != null &&
    (typeof selected.id === "string"
      ? selected.id.startsWith("custom:")
      : isPostgresIntegerId(selected.id))
  ) {
    return selected;
  }
  if (isPostgresIntegerId(item?.invoice_item_id)) {
    return {
      id: item.invoice_item_id,
      price: item?.invoice_price ?? item?.ndis_rate ?? null,
      support_item_name: item?.ndis_support_item_name ?? null,
      support_item_number: item?.ndis_support_item_number ?? null,
    };
  }
  if (selected && typeof selected === "object" && selected.id != null) {
    return selected;
  }
  return null;
};

const TimeSheetBreakDown = ({
  shiftDetails,
  setShiftDetails,
  onUpdateSuccess,
  isDisabled,
  hidePayrollFields = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues,
    watch,
    clearErrors,
  } = useForm();
  const watchFormData = watch();
  const pendingV5SplitsPayload = useRef(null);
  const committedShiftTimesRef = useRef({ start: "", end: "" });
  const billingWindowChangedRef = useRef(false);
  const pendingShiftTimeRef = useRef(null);
  const shiftTimeDebounceRef = useRef(null);
  const shiftTimePendingWarningRef = useRef(false);
  const originalShiftStateRef = useRef(null);
  const shiftTimeLocallyEditedRef = useRef(false);
  const updateConfirmedRef = useRef(false);
  const pendingSubmitDataRef = useRef(null);
  const [shiftTimeChanged, setShiftTimeChanged] = useState(false);
  const [showUpdateWarning, setShowUpdateWarning] = useState(false);
  const skipKmAutoApplyRef = useRef(false);
  const spSettings = useSelector(getSpRosteringSetting);
  const showClaimTypeFromBusinessSettings = useMemo(
    () => showClaimTypeInScheduleTimesheets(spSettings),
    [spSettings]
  );
  const showTravelFromBusinessSettings = useMemo(() => {
    const s = spSettings?.show_travel_toggle_schedule_timesheets;
    return parseBooleanVal(s) || s === 1 || s === "1";
  }, [spSettings]);
 
  const currentSpUser = useSelector(getUser);
  const catalogStates = useSelector(getServiceCatalogStates);
  const [catalogState, setCatalogState] = useState(null);
  const [serviceCategory, setServiceCategory] = useState(null);
  const [categoryItemName, setCategoryItemName] = useState(null);
  const dispatch = useDispatch();

  const [serviceTypes, setServiceTypes] = useState([]);
  const [suggestedItemNumber, setSuggestedItemNumber] = useState(null);
  const [ndisItemName, setNDISItemName] = useState(null);
  const [traveldata, setTravelData] = useState(null);
  const [travelNdisNum, setTravelNdisNum] = useState(null);
  const [participantTravelRows, setParticipantTravelRows] = useState([]);
  const isV5 = isV5MultiAssignmentTimesheet(shiftDetails);
  const legacySplitShift = isLegacySplitShift(shiftDetails);
  // Invoice billing window: basic uses invoice_time_preference (default schedule);
  // advanced/split always schedule. Timesheet breakdown still uses time_display_preference elsewhere.
  const invoiceTimePreference = resolveInvoiceTimePreference(
    shiftDetails,
    spSettings,
    { isSplitOrAdvance: isV5 || legacySplitShift }
  );
  const schedule_time = invoiceTimePreference === "scheduled";
  // Billing insight / estimate use the invoice basis (not timesheet breakdown pref).
  const timeDisplayPreference = invoiceTimePreference;
  const isAgedCareSector = spSettings?.care_sector === "aged_care";

  const startTime = schedule_time
    ? shiftDetails?.start_time
    : shiftDetails?.worker_start_time;
  const endTime = schedule_time
    ? shiftDetails?.end_time
    : shiftDetails?.worker_end_time;

  const shiftBillingUuid = getShiftUuidForBilling(shiftDetails);
  const useShiftBillingApi = Boolean(shiftBillingUuid);
  const billingLines = useMemo(
    () => getBillingLines(shiftDetails),
    [shiftDetails]
  );
  const isSplitMode = isSplitBillingMode(shiftDetails);
  const billingLocked = isBillingLocked(shiftDetails);
  const billingDisabled = isDisabled || billingLocked;
  const shiftTimeEditable = !billingDisabled;
  const showSplitAccordion = shouldShowSplitBreakdownAccordion(
    shiftDetails,
    watchFormData?.invoice_breakdown
  );
  const showServiceCategoryFields = shouldShowServiceCategoryFields(shiftDetails);

  useEffect(() => {
    dispatch(getCatalogServicesSubCategoryRequest({ isService: false }));
    apiGetRequest("/roster-invoices/service-type")
      .then((res) => {
        if (res.data) {
          const modifiedValues = res?.data?.data?.map((item) => ({
            ...item,
            label: item.name,
            value: item.id,
          }));

          setServiceTypes(modifiedValues);
        }
      })
      .catch((error) => setServiceTypes([]));
  }, []);

  const isTimesheetServiceCatNonSTA = useMemo(() => {
    return isEmpty(serviceCategory) || serviceCategory?.read_only === false;
  }, [serviceCategory]);
  useEffect(() => {
    if (shiftDetails) {
      setValue("non_billable", parseBooleanVal(shiftDetails?.invoice_non_billable));
      setValue("sleepover", shiftDetails?.sleepover);
      setValue("cancelled", parseBooleanVal(shiftDetails?.invoice_cancelled));
      setValue("km", shiftDetails?.km ?? null);
      setValue("applykm", parseBooleanVal(shiftDetails?.apply_km));
      setValue(
        "km_item_id",
        shiftDetails?.km_item_id != null ? shiftDetails.km_item_id : null
      );
      if (parseBooleanVal(shiftDetails?.km_alternative_rate)) {
        setValue(
          "km_alternate_nids_rate_amount",
          shiftDetails?.km_price ?? ""
        );
      }
      setValue("invoice_breakdown", getDefaultInvoiceBreakdown(shiftDetails));
      // Avoid km-effect forcing Apply KM on during hydrate from saved apply_km=false.
      skipKmAutoApplyRef.current = true;
      queueMicrotask(() => {
        skipKmAutoApplyRef.current = false;
      });
    }
  }, [
    setValue,
    shiftDetails?.uuid,
    shiftDetails?.default_timesheet_breakdown,
    shiftDetails?.is_v5_multi_assignment,
    shiftDetails?.is_split_shift,
    shiftDetails?.km,
    shiftDetails?.apply_km,
    shiftDetails?.km_item_id,
    shiftDetails?.km_price,
    shiftDetails?.km_alternative_rate,
    shiftDetails?.invoice_non_billable,
    shiftDetails?.invoice_cancelled,
  ]);

  useEffect(() => {
    if (!isV5) {
      setParticipantTravelRows([]);
      return;
    }
    setParticipantTravelRows(
      hydrateParticipantTravelRows(getParticipantTravelRows(shiftDetails))
    );
  }, [
    isV5,
    shiftDetails?.shift_uuid,
    shiftDetails?.uuid,
    shiftDetails?.participant_travel,
  ]);

  useEffect(() => {
    if (
      !shiftDetails ||
      shiftTimePendingWarningRef.current ||
      shiftTimeLocallyEditedRef.current
    ) {
      return;
    }
    const start = normalizeClockTimeInput(
      extractTimeFromDateTime(
        schedule_time
          ? shiftDetails?.start_time
          : shiftDetails?.worker_start_time
      )
    );
    const end = normalizeClockTimeInput(
      extractTimeFromDateTime(
        schedule_time ? shiftDetails?.end_time : shiftDetails?.worker_end_time
      )
    );
    setValue("codeStartTime", start);
    setValue("codeEndTime", end);
    committedShiftTimesRef.current = { start, end };
    billingWindowChangedRef.current = false;
    pendingV5SplitsPayload.current = null;
    originalShiftStateRef.current = null;
    setShiftTimeChanged(false);
  }, [
    shiftDetails?.uuid,
    shiftDetails?.shift_uuid,
    schedule_time,
    shiftDetails?.start_time,
    shiftDetails?.end_time,
    shiftDetails?.worker_start_time,
    shiftDetails?.worker_end_time,
    setValue,
  ]);

  useEffect(() => {
    if ((isV5 || legacySplitShift) && !watchFormData?.invoice_breakdown) {
      setValue("invoice_breakdown", true);
    }
  }, [isV5, legacySplitShift, watchFormData?.invoice_breakdown, setValue]);
  useEffect(() => {
    if (
      !shiftDetails?.is_split_shift ||
      !Array.isArray(shiftDetails?.invoiceTimesheets)
    ) {
      return;
    }

    shiftDetails.invoiceTimesheets.forEach((segment, index) => {
      if (segment?.start_time) {
        setValue(
          `start_time_${index}`,
          extractTimeFromDateTime(segment.start_time)
        );
      }
      if (segment?.end_time) {
        setValue(
          `end_time_${index}`,
          extractTimeFromDateTime(segment.end_time)
        );
      }
    });
  }, [
    shiftDetails?.is_split_shift,
    shiftDetails?.uuid,
    shiftDetails?.invoiceTimesheets,
    serviceCategory?.value,
    catalogState?.value,
    setValue,
  ]);

  useEffect(() => {
    let categoryItemNameObj;

    if (shiftDetails?.invoice_category_name) {
      categoryItemNameObj = CATEGORY_ITEM_NAME_AND_NOTES?.find(
        (x) => x.value === shiftDetails?.invoice_category_name
      );

      setCategoryItemName(categoryItemNameObj);
    } else if (
      shiftDetails &&
      !shiftDetails?.invoice_category_name &&
      shiftDetails?.participants?.length > 0
    ) {
      const categoryItemNameObj = CATEGORY_ITEM_NAME_AND_NOTES?.find(
        (categoryItem) =>
          shiftDetails?.participants?.some(
            (participant) =>
              participant.roster_invoicing_item === categoryItem.value
          )
      );
      setCategoryItemName(categoryItemNameObj);
    }

    if (
      shiftDetails &&
      shiftDetails?.invoice_service_type_id &&
      serviceTypes?.length > 0
    ) {
      const value = serviceTypes?.find(
        (service) => service?.value === shiftDetails?.invoice_service_type_id
      );

      setServiceCategory(value);
    }
    if (shiftDetails?.state_id && catalogStates) {
      const stateObj = catalogStates.find(
        (x) => x.value === shiftDetails?.state_id
      );

      setCatalogState({ ...stateObj });
    } else if (
      shiftDetails &&
      !shiftDetails?.state_id &&
      shiftDetails?.participants?.length > 0
    ) {
      const firstParticipantStateId = shiftDetails?.participants?.find(
        (item) => item?.state_id
      )?.state_id;

      const firstMatchingState = catalogStates?.find(
        (state) => state?.value === firstParticipantStateId
      );

      setCatalogState({ ...firstMatchingState });
    }
  }, [shiftDetails, shiftDetails?.uuid, catalogStates, serviceTypes]);

  
  const [itemNumber, setItemNumber] = useState([]);
  const [loading, setLoading] = useState(false);

  const getInvoiceItemOptionValue = (item) => {
    if (item?.is_custom && item?.custom_line_item_uuid) {
      return `custom:${item.custom_line_item_uuid}`;
    }
    return item?.id;
  };

  const mapInvoiceItemOption = (item) => ({
    label: item.support_item_name + " - " + item?.support_item_number,
    value: getInvoiceItemOptionValue(item),
    price: item.price,
    number: item?.support_item_number,
    name: item?.support_item_name,
    isCustom: Boolean(item?.is_custom),
  });

  useEffect(() => {
    const fetchItemNumber = async () => {
      if (isV5) {
        return;
      }
      if (
        serviceCategory?.value &&
        catalogState?.value &&
        categoryItemName?.value &&
        startTime &&
        endTime
      ) {
        setLoading(true);
        const url = `${URL_ROSTERING_INVOICING_GET_ITEM_NUMBER_ON_TIMESHEET}/?invoice_service_type_id=${
          serviceCategory?.value
        }&state_id=${catalogState?.value}&uuid=${
          shiftDetails?.uuid || ""
        }&sleepover=${
          watchFormData?.sleepover || false
        }&invoice_category_name=${
          categoryItemName?.value
        }&start_time=${startTime}&end_time=${endTime}`;

        try {
          const res = await apiGetRequest(url);
          if (res.status === 200) {
            setLoading(false);
            const responseDate = res?.data?.data?.items;
            const selectedItem = res?.data?.data?.selected;

            const itemNumberOptions = responseDate?.map(mapInvoiceItemOption) || [];

            // Selected custom items can be returned even when filtered out of
            // `items` (state/funding_type). Keep them in options so budget
            // check + dropdown selection stay in sync on first load.
            if (selectedItem != null) {
              const selectedValue = getInvoiceItemOptionValue(selectedItem);
              if (
                selectedValue != null &&
                !itemNumberOptions.some(
                  (opt) => String(opt.value) === String(selectedValue)
                )
              ) {
                itemNumberOptions.unshift(mapInvoiceItemOption(selectedItem));
              }
            }

            setItemNumber(itemNumberOptions);
            setSuggestedItemNumber(res?.data?.data?.selected);

            const obj = {
              label: selectedItem?.support_item_name
                ? selectedItem?.support_item_name +
                  " - " +
                  selectedItem?.support_item_number
                : null,
              number: selectedItem?.support_item_number ?? null,
              value: getInvoiceItemOptionValue(selectedItem) ?? null,
              price: selectedItem?.price ?? null,
            };
            if (selectedItem?.is_alternate) {
              obj.is_alternate = true;
              obj.alternate_price = selectedItem?.alternate_price ?? null;
            }
            setNDISItemName({ ...obj });

            if (parseBooleanVal(spSettings?.apply_alternate_rate)) {
              setValue("invoice_ndis_rate", "alternate");
              setValue("rate_amount", spSettings?.default_alternate_rate);
            } else if (obj.price) {
              if (obj?.is_alternate) {
                setValue("invoice_ndis_rate", "alternate");
                setValue("rate_amount", obj?.alternate_price);
              } else {
                setValue("invoice_ndis_rate", obj.price);
                setValue("rate_amount", obj.price);
              }
            }
          }
        } catch (error) {
          setLoading(false);
          //   console.error("Error fetching item number:", error);
        }
      } else {
        setItemNumber([]);
        setSuggestedItemNumber(null);
        setNDISItemName(null);
      }
    };

    fetchItemNumber();
  }, [
    isV5,
    serviceCategory?.value,
    catalogState?.value,
    categoryItemName?.value,
    watchFormData?.sleepover,
    startTime,
    endTime,
    shiftDetails?.uuid,
  ]);
  const [timesheetBreakdown, setTimesheetBreakdown] = useState(null);
  const [breakdownLoader, setBreakdownLoader] = useState(false);
  const isFirstRender = useRef(true);
  const isFirstAnyValueChanged = useRef(true);
  const breakdownFetchGenerationRef = useRef(0);
  const basicBreakdownCatalogKeyRef = useRef(null);
  const basicBreakdownCatalogClearBaselineRef = useRef(null);
  const breakdownSegmentCountRef = useRef(0);
  // After service/state/category/sleepover change, do not re-apply stale saved codes
  // onto the regenerated preview (user intentionally cleared NDIS fields).
  const suppressSavedBillingOverlayRef = useRef(false);

  const getBasicBreakdownCatalogKey = () =>
    [
      serviceCategory?.value ?? "",
      catalogState?.value ?? "",
      categoryItemName?.value ?? "",
      // Normalize so undefined/false both hash as 0 during async hydrate.
      parseBooleanVal(watchFormData?.sleepover) ? "1" : "0",
    ].join("|");

  const isBasicBreakdownCatalogKeyComplete = (catalogKey) => {
    const [serviceId, stateId, categoryName] = String(catalogKey ?? "").split(
      "|"
    );
    return Boolean(serviceId && stateId && categoryName);
  };

  const clearBasicBreakdownNdisFields = (segmentCount) => {
    const count = Math.max(
      segmentCount,
      breakdownSegmentCountRef.current,
      billingLines?.length ?? 0,
      timesheetBreakdown?.length ?? 0
    );
    for (let i = 0; i < count; i++) {
      setValue(`invoice_item_id_${i}`, null);
      setValue(`invoice_ndis_rate_${i}`, "");
      setValue(`rate_amount_${i}`, "");
      setValue(`applied_rate_${i}`, "");
      setValue(`invoice_item_name_${i}`, "");
      // Active hours bill against the same catalogue — its line item and rate are stale too.
      setValue(`invoice_item_id_active_hours${i}`, null);
      setValue(`invoice_item_name_active_hours${i}`, "");
      setValue(`invoice_ndis_rate_active_hours${i}`, "");
      setValue(`rate_amount_active_hours${i}`, "");
      setValue(`applied_rate_active_hours${i}`, "");
    }
  };

  useEffect(() => {
    if (timesheetBreakdown?.length) {
      breakdownSegmentCountRef.current = timesheetBreakdown.length;
    }
  }, [timesheetBreakdown?.length]);

  useEffect(() => {
    if (isV5 || !watchFormData?.invoice_breakdown) return;

    const catalogKey = getBasicBreakdownCatalogKey();
    const prevKey = basicBreakdownCatalogKeyRef.current;

    if (prevKey === null) {
      basicBreakdownCatalogKeyRef.current = catalogKey;
      return;
    }
    if (prevKey === catalogKey) return;

    // Reopen / first paint: catalog fields populate async from shiftDetails
    // ("" → "14|1|s|0"). That is NOT a user change — suppressing here dropped
    // saved alternate_rate overlays when returning from the list.
    if (
      !isBasicBreakdownCatalogKeyComplete(prevKey) ||
      !isBasicBreakdownCatalogKeyComplete(catalogKey)
    ) {
      basicBreakdownCatalogKeyRef.current = catalogKey;
      return;
    }

    basicBreakdownCatalogKeyRef.current = catalogKey;
    suppressSavedBillingOverlayRef.current = true;
    clearBasicBreakdownNdisFields(breakdownSegmentCountRef.current);
  }, [
    isV5,
    serviceCategory?.value,
    catalogState?.value,
    categoryItemName?.value,
    watchFormData?.sleepover,
    watchFormData?.invoice_breakdown,
  ]);
  const breakdownFormScopeRef = useRef("");

  useEffect(() => {
    breakdownFormScopeRef.current = "";
    if (
      !watchFormData?.invoice_breakdown ||
      shiftDetails?.is_split_shift ||
      isV5
    ) {
      return;
    }

    const catalogKey = getBasicBreakdownCatalogKey();
    const prevKey = basicBreakdownCatalogClearBaselineRef.current;

    if (prevKey === null) {
      basicBreakdownCatalogClearBaselineRef.current = catalogKey;
      return;
    }
    if (prevKey === catalogKey) return;

    // Same reopen hydrate guard as overlay suppress — do not wipe form while
    // service/state/category are still loading from the billing-allocation GET.
    if (
      !isBasicBreakdownCatalogKeyComplete(prevKey) ||
      !isBasicBreakdownCatalogKeyComplete(catalogKey)
    ) {
      basicBreakdownCatalogClearBaselineRef.current = catalogKey;
      return;
    }

    basicBreakdownCatalogClearBaselineRef.current = catalogKey;
    clearBreakdownFormFields(setValue);
  }, [
    serviceCategory?.value,
    catalogState?.value,
    categoryItemName?.value,
    watchFormData?.sleepover,
    watchFormData?.invoice_breakdown,
    shiftDetails?.is_split_shift,
    isV5,
    setValue,
  ]);

  useEffect(() => {
    if (
      isV5 ||
      shiftDetails?.is_split_shift ||
      !watchFormData?.invoice_breakdown ||
      !Array.isArray(timesheetBreakdown)
    ) {
      return;
    }

    const scopeKey = [
      serviceCategory?.value ?? "",
      catalogState?.value ?? "",
      categoryItemName?.value ?? "",
      shiftDetails?.uuid ?? "",
      timesheetBreakdown
        .map((row, rowIndex) =>
          getBreakdownSegmentHydrationKey(
            row,
            rowIndex,
            serviceCategory?.value
          )
        )
        .join(";"),
    ].join("|");

    if (breakdownFormScopeRef.current === scopeKey) return;
    breakdownFormScopeRef.current = scopeKey;
    clearBreakdownFormFields(setValue);
  }, [
    serviceCategory?.value,
    catalogState?.value,
    categoryItemName?.value,
    shiftDetails?.uuid,
    timesheetBreakdown,
    watchFormData?.invoice_breakdown,
    isV5,
    setValue,
  ]);

  useEffect(() => {
    isFirstRender.current = true;
    isFirstAnyValueChanged.current = true;
    breakdownFetchGenerationRef.current = 0;
    basicBreakdownCatalogKeyRef.current = null;
    basicBreakdownCatalogClearBaselineRef.current = null;
    breakdownSegmentCountRef.current = 0;
    suppressSavedBillingOverlayRef.current = false;
    originalShiftStateRef.current = null;
    shiftTimeLocallyEditedRef.current = false;
    updateConfirmedRef.current = false;
    pendingSubmitDataRef.current = null;
    setShiftTimeChanged(false);
    setShowUpdateWarning(false);
    setItemNumber([]);
    setSuggestedItemNumber(null);
    setNDISItemName(null);
    setTimesheetBreakdown(null);
    clearNdisLineItemsCache();
  }, [shiftDetails?.uuid, spSettings?.care_sector]);

  const getSplitSuggestedItem = (item) => {
    const customUuid = item?.custom_line_item_uuid ?? item?.custom_line_item?.uuid;
    if (customUuid) {
      return {
        id: `custom:${customUuid}`,
        price: item?.invoice_price,
        support_item_name:
          item?.custom_line_item?.description ?? item?.ndis_support_item_name,
        support_item_number:
          item?.custom_line_item?.unique_code ?? item?.ndis_support_item_number,
      };
    }

    return {
      id: item?.invoice_item_id,
      price: item?.invoice_price,
      support_item_name: item?.ndis_support_item_name,
      support_item_number: item?.ndis_support_item_number,
    };
  };

  const getSplitItemNumberOptions = (item) => {
    const ndisItems = (item?.item_numbers?.items || []).map(mapInvoiceItemOption);

    const customUuid = item?.custom_line_item_uuid ?? item?.custom_line_item?.uuid;
    if (!customUuid) {
      return mergeBreakdownSavedLineItemOption(ndisItems, item);
    }

    const customValue = `custom:${customUuid}`;
    const hasCustom = ndisItems.some((opt) => String(opt.value) === customValue);
    const withCustom = hasCustom
      ? ndisItems
      : [
          ...ndisItems,
          {
            label: `${item?.custom_line_item?.description ?? item?.ndis_support_item_name ?? "Custom line item"} - ${item?.custom_line_item?.unique_code ?? item?.ndis_support_item_number ?? ""}`.trim(),
            value: customValue,
            price: item?.invoice_price,
            number: item?.custom_line_item?.unique_code ?? item?.ndis_support_item_number ?? "",
            isCustom: true,
          },
        ];
    return mergeBreakdownSavedLineItemOption(withCustom, item);
  };

  useEffect(() => {
    const fetchTimesheetBreakdown = async () => {
      if (isV5) return;

      // Basic split is 1:1 with split_shifts. DEN preview (day/evening/night)
      // can return extra uncoded rows and must not replace the split list.
      if (legacySplitShift) {
        if (Array.isArray(billingLines) && billingLines.length > 0) {
          setTimesheetBreakdown(billingLines);
        }
        isFirstRender.current = false;
        return;
      }

      const resolvedServiceTypeId =
        serviceCategory?.value || shiftDetails?.invoice_service_type_id;
      const resolvedStateId = catalogState?.value || shiftDetails?.state_id;
      const resolvedCategoryName =
        categoryItemName?.value || shiftDetails?.invoice_category_name;

      const canFetchShiftPreview =
        useShiftBillingApi &&
        startTime &&
        endTime &&
        watchFormData?.invoice_breakdown === true;

      const canFetchLegacyPreview =
        !useShiftBillingApi &&
        resolvedServiceTypeId &&
        resolvedStateId &&
        resolvedCategoryName &&
        startTime &&
        endTime &&
        watchFormData?.invoice_breakdown === true;

      if (!canFetchShiftPreview && !canFetchLegacyPreview) {
        return;
      }

      const hasPersistedBasicBreakdown =
        !isV5 &&
        !legacySplitShift &&
        watchFormData?.invoice_breakdown === true &&
        Array.isArray(billingLines) &&
        billingLines.length > 1 &&
        !savedBreakdownWindowsAreCollapsedDuplicates(billingLines);

      // Only hydrate from saved lines on passive first paint (reopen). A manual
      // toggle ON must always call billing-breakdown-preview — saved allocation
      // lines are not day/evening/night segments until preview runs.
      if (
        isFirstRender.current &&
        isFirstAnyValueChanged.current &&
        billingLines?.length > 0 &&
        (legacySplitShift ||
          (isV5 && watchFormData?.invoice_breakdown) ||
          hasPersistedBasicBreakdown)
      ) {
        isFirstRender.current = false;
        setTimesheetBreakdown(billingLines);
        return;
      }

      setBreakdownLoader(true);
      if (!isV5) {
        // Keep rows during refresh when saved codes will be re-overlaid onto preview.
        // Clearing here raced with post-save alternate_rate sync and blanked the UI.
        const keepRowsDuringFetch =
          !legacySplitShift &&
          watchFormData?.invoice_breakdown === true &&
          !suppressSavedBillingOverlayRef.current &&
          Array.isArray(billingLines) &&
          billingLines.some(
            (row) =>
              row?.invoice_item_id ||
              row?.custom_line_item_uuid ||
              row?.custom_line_item?.uuid
          );
        if (!keepRowsDuringFetch) {
          setTimesheetBreakdown(null);
        }
      }
      const fetchGeneration = ++breakdownFetchGenerationRef.current;
      const breakdownRequested = watchFormData?.invoice_breakdown === true;
      // Basic shifts: SP time preference selects scheduled vs actual window for preview.
      const url = useShiftBillingApi
        ? `${URL_V5_SHIFT_BILLING_BREAKDOWN_PREVIEW(shiftBillingUuid)}/?invoice_service_type_id=${
            serviceCategory?.value || shiftDetails?.invoice_service_type_id || ""
          }&state_id=${catalogState?.value || ""}&sleepover=${
            watchFormData?.sleepover || false
          }&start_time=${startTime || ""}&end_time=${endTime || ""}&invoice_category_name=${
            categoryItemName?.value || shiftDetails?.invoice_category_name || ""
          }&default_timesheet_breakdown=${breakdownRequested}`
        : `${URL_ROSTER_INVOICING_TIMESHEET_BREAKDOWN}/?invoice_service_type_id=${
            resolvedServiceTypeId || ""
          }&state_id=${resolvedStateId || ""}&uuid=${
            shiftDetails?.uuid || ""
          }&sleepover=${watchFormData?.sleepover || false}&start_time=${
            startTime || ""
          }&end_time=${endTime || ""}&invoice_category_name=${
            resolvedCategoryName || ""
          }&default_timesheet_breakdown=${breakdownRequested}`;

      try {
        const res = await apiGetRequest(url);

        if (fetchGeneration !== breakdownFetchGenerationRef.current) {
          return;
        }

        if (res.status === 200) {
          const previewRows = useShiftBillingApi
            ? parseShiftBreakdownPreviewResponse(res)
            : res?.data?.data;

          // Basic auto-breakdown: overlay saved codes/alternate rates. Saved
          // segment clocks win after a user edit; preview clocks are only used
          // to repair collapsed "every row = full shift" lines.
          // Skip overlay after category/state/sleepover change (fields were cleared).
          const isBasicInvoiceBreakdown =
            !isV5 &&
            !legacySplitShift &&
            watchFormData?.invoice_breakdown === true;

          isFirstRender.current = false;
          if (isBasicInvoiceBreakdown) {
            const nextRows = suppressSavedBillingOverlayRef.current
              ? previewRows
              : mergePreviewWithSavedBillingLines(previewRows, billingLines);
            setTimesheetBreakdown(nextRows);
          } else {
            setTimesheetBreakdown(previewRows);
          }

          setBreakdownLoader(false);
        }
      } catch (error) {
        if (fetchGeneration !== breakdownFetchGenerationRef.current) {
          return;
        }
        setBreakdownLoader(false);
        console.error("Error fetching timesheet breakdown:", error);
        setTimesheetBreakdown(null);
      }
    };

    fetchTimesheetBreakdown();
  }, [
    serviceCategory?.value,
    catalogState?.value,
    categoryItemName?.value,
    watchFormData?.sleepover,
    startTime,
    endTime,
    shiftDetails?.uuid,
    shiftDetails?.shift_uuid,
    shiftDetails?.is_v5_multi_assignment,
    shiftDetails?.v5_shift_billing,
    shiftDetails?.invoice_service_type_id,
    shiftDetails?.invoice_category_name,
    watchFormData?.invoice_breakdown,
    useShiftBillingApi,
    shiftBillingUuid,
    isV5,
    legacySplitShift,
    billingLines,
  ]);

  const splitShiftBreakdownData = useMemo(
    () => buildSplitBreakdownRows(shiftDetails, timesheetBreakdown),
    [shiftDetails, timesheetBreakdown]
  );

  useEffect(() => {
    if (!isAgedCareSector && (shiftDetails?.apply_km || watchFormData?.applykm)) {
      fetchApplyKmDetails();
    }
  }, [
    isAgedCareSector,
    shiftDetails?.apply_km,
    shiftDetails?.status,
    serviceCategory?.value,
    catalogState?.value,
    watchFormData?.applykm,
  ]);

  useEffect(() => {
    if (!parseBooleanVal(watchFormData?.applykm)) {
      setTravelNdisNum(null);
      setTravelData([]);
      setValue("km_item_id", null);
    }
  }, [watchFormData?.applykm]);

  useEffect(() => {
    if (isAgedCareSector) {
      setValue("applykm", false);
      setValue("km_item_id", null);
      setTravelNdisNum(null);
      setTravelData([]);
      return;
    }

    const pattern = /^\d*[0-9][.]?\d*$/;
    const kmStr = String(watchFormData?.km ?? "");
    if (!pattern.test(kmStr)) {
      return;
    }

    clearErrors("km");

    // During hydrate from shiftDetails, preserve saved apply_km (including false).
    if (skipKmAutoApplyRef.current) {
      if (parseBooleanVal(watchFormData?.applykm)) {
        fetchApplyKmDetails();
      }
      return;
    }

    if (parseBooleanVal(spSettings?.apply_km_to_invoice)) {
      setValue("applykm", true);
      fetchApplyKmDetails();
    }
  }, [watchFormData?.km, isAgedCareSector]);

  const fetchApplyKmDetails = async () => {
    if (shiftDetails?.uuid) {
      setLoading(true);
      const url = `${URL_ROSTERING_INVOICING_GET_TRAVEL_DETAILS}/?invoice_service_type_id=${
        serviceCategory?.value || ""
      }&state_id=${catalogState?.value || ""}&uuid=${shiftDetails?.uuid || ""}`;

      try {
        const res = await apiGetRequest(url);
        if (res.status === 200) {
          const responseData = Array.isArray(res?.data?.data?.items)
            ? res?.data?.data?.items
            : [res?.data?.data?.items];
          const isSelected = res?.data?.data?.selected;
          const options = (responseData || [])
            .filter(Boolean)
            .map((x) => ({
              ...x,
              label: x.support_item_number,
              value: x.id,
            }));

          setTravelData(options);

          const preferredId =
            watchFormData?.km_item_id ?? shiftDetails?.km_item_id ?? null;
          const byId = preferredId
            ? options.find((x) => String(x.id) === String(preferredId))
            : null;

          if (isSelected && isSelected?.support_item_number) {
            setTravelNdisNum({
              ...isSelected,
              value: isSelected?.id,
              label: isSelected?.support_item_number,
              km_alternative_price: isSelected?.km_alternative_price,
              km_alternative_rate: isSelected?.km_alternative_rate,
            });
            setValue("km_item_id", isSelected?.id);
          } else if (byId) {
            setTravelNdisNum({
              ...byId,
              value: byId.id,
              label: byId.support_item_number,
              km_alternative_price:
                byId.km_alternative_price ??
                (parseBooleanVal(shiftDetails?.km_alternative_rate)
                  ? shiftDetails?.km_price
                  : byId.km_alternative_price),
              km_alternative_rate:
                byId.km_alternative_rate ??
                parseBooleanVal(shiftDetails?.km_alternative_rate),
            });
            setValue("km_item_id", byId.id);
          } else if (options.length > 0) {
            const data = options.find(
              (x) => x.support_item_number === shiftDetails?.default_km_code
            );

            if (data) {
              setTravelNdisNum({
                ...data,
                value: data?.id,
                label: data?.support_item_number,
                km_alternative_price: data?.km_alternative_price,
                km_alternative_rate: data?.km_alternative_rate,
              });
              setValue("km_item_id", data?.id);
            }
          }
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
        console.error("Error fetching travel detail:", error);
        toast.error("Error Fetching Travel Detail");
      }
    }
  };

  const splitBreakdownActive = shouldShowSplitBreakdownAccordion(
    shiftDetails,
    watchFormData?.invoice_breakdown
  );
  const splitShiftBreakdownList =
    splitBreakdownActive && billingLines?.length > 0
      ? billingLines
      : shiftDetails?.is_split_shift &&
          shiftDetails?.invoiceTimesheets?.length > 0
        ? shiftDetails.invoiceTimesheets
        : null;
  const breakdownItemsIdentityKey = useMemo(() => {
    const items = splitShiftBreakdownList ?? timesheetBreakdown ?? [];
    if (!Array.isArray(items) || items.length === 0) return "";
    return items
      .map((row, rowIdx) => {
        const itemsSig = (row?.item_numbers?.items ?? [])
          .map((el) => `${el.id}:${el.price ?? ""}`)
          .join(",");
        return `${getAccordionItemNumberKey(row, rowIdx)}|${itemsSig}`;
      })
      .join(";");
  }, [splitShiftBreakdownList, timesheetBreakdown]);
  const fallbackItemNumberSig = useMemo(
    () => getItemNumberOptionsSignature(itemNumber),
    [itemNumber]
  );
  const accordionItemNumbersByKey = useMemo(() => {
    const items = splitShiftBreakdownList ?? timesheetBreakdown ?? [];
    if (!Array.isArray(items) || items.length === 0) return new Map();
    const map = new Map();
    items.forEach((item, idx) => {
      map.set(
        getAccordionItemNumberKey(item, idx),
        legacySplitShift || isV5
          ? getSplitItemNumberOptions(item)
          : resolveAccordionItemNumbers(item, itemNumber)
      );
    });
    return map;
  }, [breakdownItemsIdentityKey, fallbackItemNumberSig, legacySplitShift, isV5]);
  const activeBreakdownForDuration = splitBreakdownActive
    ? splitShiftBreakdownList ?? splitShiftBreakdownData
    : timesheetBreakdown;
  const totalItemsDuration = useMemo(() => {
    if (
      !watchFormData?.invoice_breakdown ||
      !Array.isArray(activeBreakdownForDuration) ||
      activeBreakdownForDuration.length === 0
    ) {
      return null;
    }

    // Advance/V5 and legacy basic split: sum from form clocks so a window
    // change (collapse) is reflected immediately — not stale row.duration.
    if (isV5) {
      const shiftStart = watchFormData.codeStartTime;
      const shiftEnd = watchFormData.codeEndTime;
      const fullDayByParticipant = new Set();
      let totalMinutes = 0;
      let usedFormClocks = false;
      activeBreakdownForDuration.forEach((row, i) => {
        const start = watchFormData[`start_time_${i}`];
        const end = watchFormData[`end_time_${i}`];
        if (!start || !end) return;
        usedFormClocks = true;
        const ptKey = getBreakdownParticipantKey(shiftDetails, i, row);
        const minutes = getAdvanceSegmentWindowMinutes(
          start,
          end,
          shiftStart,
          shiftEnd,
          { allowSameClockFullDay: !fullDayByParticipant.has(ptKey) }
        );
        if (minutes === 24 * 60) fullDayByParticipant.add(ptKey);
        if (minutes != null && minutes > 0) totalMinutes += minutes;
      });
      if (usedFormClocks) {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
          2,
          "0"
        )}:00`;
      }
    }

    if (legacySplitShift) {
      let totalMinutes = 0;
      let usedFormClocks = false;
      activeBreakdownForDuration.forEach((_, i) => {
        const start = watchFormData[`start_time_${i}`];
        const end = watchFormData[`end_time_${i}`];
        if (!start || !end) return;
        usedFormClocks = true;
        const hhmm = getBasicShiftDurationHHMM(start, end);
        if (!hhmm) return;
        const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10) || 0);
        totalMinutes += h * 60 + m;
      });
      if (usedFormClocks) {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
          2,
          "0"
        )}:00`;
      }
    }

    // Basic auto-breakdown: target total is the shift window (HH:mm), not the
    // sum of preview segment duration strings which can lose a minute to seconds.
    if (!isV5 && !legacySplitShift) {
      const codeStart = normalizeClockTimeInput(watchFormData?.codeStartTime);
      const codeEnd = normalizeClockTimeInput(watchFormData?.codeEndTime);
      const hhmm = resolveBasicShiftDurationHHMM(codeStart, codeEnd, {
        spansNextCalendarDay: shiftClocksSpanNextCalendarDay(startTime, endTime),
      });
      if (hhmm != null && hhmm !== "") {
        return `${hhmm}:00`;
      }
    }

    return calculateTotalDuration(activeBreakdownForDuration);
  }, [
    watchFormData,
    activeBreakdownForDuration,
    isV5,
    legacySplitShift,
    shiftDetails,
    startTime,
    endTime,
  ]);
  const splitShiftBreakdownCount =
    splitShiftBreakdownList?.length ?? splitShiftBreakdownData?.length ?? 0;
  const breakdownSegmentCount = splitBreakdownActive
    ? splitShiftBreakdownCount
    : timesheetBreakdown?.length ?? 0;
  const breakdownDurationLabel = useMemo(() => {
    if (!totalItemsDuration) return null;
    const parts = String(totalItemsDuration).split(":");
    const h = parts[0] || "0";
    const m = parts[1] || "00";
    return `${h}h ${m}m total`;
  }, [totalItemsDuration]);
  const showBreakdownPanel =
    (splitBreakdownActive && splitShiftBreakdownCount > 0) ||
    watchFormData?.invoice_breakdown;
  const isSleepoverEligibleServiceName = (name) =>
    name === "Self-Care (Deprecated)" ||
    name === "Home and Living" ||
    name === "Assistance with Daily Life";
  const sleepoverEligibleCategory = useMemo(
    () => isSleepoverEligibleServiceName(serviceCategory?.name),
    [serviceCategory?.name]
  );
  const [submitLoader, setSubmitLoader] = useState(false);
  const [shiftTimePendingWarning, setShiftTimePendingWarning] = useState(false);
  const [pendingShiftTimeChange, setPendingShiftTimeChange] = useState(null);

  const showShiftTimePendingWarning = (nextStart, nextEnd) => {
    const payload = { nextStart, nextEnd };
    pendingShiftTimeRef.current = payload;
    setPendingShiftTimeChange(payload);
    shiftTimePendingWarningRef.current = true;
    setShiftTimePendingWarning(true);
  };

  const clearShiftTimePendingWarning = () => {
    shiftTimePendingWarningRef.current = false;
    setShiftTimePendingWarning(false);
    setPendingShiftTimeChange(null);
    pendingShiftTimeRef.current = null;
  };

  const clearShiftTimeDebounce = () => {
    if (shiftTimeDebounceRef.current) {
      clearTimeout(shiftTimeDebounceRef.current);
      shiftTimeDebounceRef.current = null;
    }
  };

  useEffect(
    () => () => {
      clearShiftTimeDebounce();
    },
    []
  );

  const resolveBreakdownRowsForShiftEdit = () => {
    if (splitShiftBreakdownData?.length > 0) {
      return splitShiftBreakdownData;
    }
    if (timesheetBreakdown?.length > 0) {
      return timesheetBreakdown;
    }
    const built = buildSplitBreakdownRows(shiftDetails, timesheetBreakdown);
    if (built?.length > 0) {
      return built;
    }
    const lines = getBillingLines(shiftDetails);
    return lines?.length > 0 ? lines : [];
  };

  const hasBillingSegmentsForShiftEdit = () =>
    breakdownSegmentCount > 0 ||
    getBillingSplits(shiftDetails).length > 0 ||
    getBillingLines(shiftDetails).length > 0;

  const syncBreakdownFormTimes = (nextStart, nextEnd, rowCount) => {
    const prevCount = Math.max(
      breakdownSegmentCountRef.current || 0,
      rowCount || 0
    );
    for (let i = 0; i < rowCount; i += 1) {
      setValue(`start_time_${i}`, nextStart);
      setValue(`end_time_${i}`, nextEnd);
      clearErrors(`start_time_${i}`);
      clearErrors(`end_time_${i}`);
    }
    // Drop leftover clocks from removed segments after collapse.
    for (let i = rowCount; i < prevCount; i += 1) {
      setValue(`start_time_${i}`, "");
      setValue(`end_time_${i}`, "");
      clearErrors(`start_time_${i}`);
      clearErrors(`end_time_${i}`);
    }
    breakdownSegmentCountRef.current = rowCount;
  };

  const cloneBreakdownRows = (rows) => {
    if (!Array.isArray(rows)) return null;
    try {
      return JSON.parse(JSON.stringify(rows));
    } catch (e) {
      return rows.map((row) => ({ ...row }));
    }
  };

  // Snapshot shift window + billing breakdown before the first time change
  // so the user can revert before updating allocation.
  const captureOriginalShiftState = () => {
    if (originalShiftStateRef.current) return;
    const activeRows = resolveBreakdownRowsForShiftEdit();
    const rowCount = activeRows?.length || 0;
    const formTimes = {};
    for (let i = 0; i < rowCount; i += 1) {
      formTimes[`start_time_${i}`] = getValues(`start_time_${i}`);
      formTimes[`end_time_${i}`] = getValues(`end_time_${i}`);
      formTimes[`active_hours_${i}`] = getValues(`active_hours_${i}`);
    }
    let shiftDetailsSnapshot = shiftDetails;
    try {
      shiftDetailsSnapshot = JSON.parse(JSON.stringify(shiftDetails));
    } catch (e) {
      shiftDetailsSnapshot = shiftDetails;
    }
    originalShiftStateRef.current = {
      times: { ...committedShiftTimesRef.current },
      breakdown: cloneBreakdownRows(
        timesheetBreakdown?.length > 0 ? timesheetBreakdown : activeRows
      ),
      shiftDetails: shiftDetailsSnapshot,
      splitsPayload: pendingV5SplitsPayload.current,
      formTimes,
    };
  };

  const applyCodeShiftTimeChange = (nextStart, nextEnd, { collapseSplits = false } = {}) => {
    const normStart = normalizeClockTimeInput(nextStart);
    const normEnd = normalizeClockTimeInput(nextEnd);
    captureOriginalShiftState();
    const prevCommitted = committedShiftTimesRef.current;
    if (
      normStart !== normalizeClockTimeInput(prevCommitted.start) ||
      normEnd !== normalizeClockTimeInput(prevCommitted.end)
    ) {
      billingWindowChangedRef.current = true;
    }
    const activeRows = resolveBreakdownRowsForShiftEdit();
    const { startDt, endDt } = buildShiftWindowDatetimes(
      shiftDetails,
      normStart,
      normEnd,
      schedule_time
    );

    setValue("codeStartTime", normStart);
    setValue("codeEndTime", normEnd);
    committedShiftTimesRef.current = { start: normStart, end: normEnd };

    // Basic + auto-breakdown (non-split DEN only): update root window and
    // regenerate day/evening/night via preview. Legacy basic split must follow
    // the Advance path and collapse to one segment per participant.
    const basicAutoBreakdown =
      !isV5 &&
      !legacySplitShift &&
      Boolean(watchFormData?.invoice_breakdown);

    if (basicAutoBreakdown) {
      pendingV5SplitsPayload.current = null;
      clearBreakdownFormFields(setValue);
      setTimesheetBreakdown(null);
      isFirstRender.current = false;
      isFirstAnyValueChanged.current = false;
      if (typeof setShiftDetails === "function") {
        setShiftDetails((prev) =>
          applyShiftWindowToShiftDetails(
            prev,
            startDt,
            endDt,
            schedule_time,
            [],
            { resetSegments: false }
          )
        );
      }
      const original = originalShiftStateRef.current;
      const changedFromOriginal =
        original &&
        (normStart !== normalizeClockTimeInput(original.times.start) ||
          normEnd !== normalizeClockTimeInput(original.times.end));
      shiftTimeLocallyEditedRef.current = Boolean(changedFromOriginal);
      setShiftTimeChanged(Boolean(changedFromOriginal));
      return;
    }

    let updatedRows = activeRows;
    if (activeRows.length > 0) {
      updatedRows = collapseSplits
        ? collapseBreakdownRowsToShiftWindow(
            shiftDetails,
            activeRows,
            startDt,
            endDt
          )
        : activeRows.map((row) => ({
            ...row,
            start_time: startDt,
            end_time: endDt,
            duration: calculateDuration(startDt, endDt),
          }));
    }

    if (updatedRows.length > 0) {
      setTimesheetBreakdown(updatedRows);
      isFirstAnyValueChanged.current = false;
    }

    if (isV5 && updatedRows.length > 0) {
      const splitsPayload = buildV5SplitsStorePayload(shiftDetails, updatedRows);
      pendingV5SplitsPayload.current =
        splitsPayload.splits.length > 0 ? splitsPayload : null;
    } else if (!collapseSplits) {
      pendingV5SplitsPayload.current = null;
    }

    syncBreakdownFormTimes(normStart, normEnd, updatedRows.length);

    if (typeof setShiftDetails === "function") {
      setShiftDetails((prev) =>
        applyShiftWindowToShiftDetails(
          prev,
          startDt,
          endDt,
          schedule_time,
          updatedRows,
          { resetSegments: collapseSplits }
        )
      );
    }

    const original = originalShiftStateRef.current;
    const changedFromOriginal =
      original &&
      (normStart !== normalizeClockTimeInput(original.times.start) ||
        normEnd !== normalizeClockTimeInput(original.times.end));
    shiftTimeLocallyEditedRef.current = Boolean(changedFromOriginal);
    setShiftTimeChanged(Boolean(changedFromOriginal));
  };

  const handleRevertShiftTime = () => {
    const snap = originalShiftStateRef.current;
    if (!snap) return;

    clearShiftTimeDebounce();
    clearShiftTimePendingWarning();

    setValue("codeStartTime", snap.times.start);
    setValue("codeEndTime", snap.times.end);
    committedShiftTimesRef.current = { ...snap.times };
    billingWindowChangedRef.current = false;
    pendingV5SplitsPayload.current = snap.splitsPayload ?? null;

    if (snap.breakdown) {
      setTimesheetBreakdown(cloneBreakdownRows(snap.breakdown));
    }

    Object.entries(snap.formTimes || {}).forEach(([key, value]) => {
      setValue(key, value);
    });

    if (snap.shiftDetails && typeof setShiftDetails === "function") {
      setShiftDetails(snap.shiftDetails);
    }

    originalShiftStateRef.current = null;
    shiftTimeLocallyEditedRef.current = false;
    setShiftTimeChanged(false);
    isFirstAnyValueChanged.current = false;
    toast.success(
      "Shift time and billing breakdown reverted to the original window."
    );
  };

  const evaluateShiftTimeChange = (nextStart, nextEnd) => {
    const activeRows = resolveBreakdownRowsForShiftEdit();
    const needsConfirm = shouldConfirmShiftTimeChange(
      nextStart,
      nextEnd,
      committedShiftTimesRef.current,
      activeRows,
      { hasBillingSegments: hasBillingSegmentsForShiftEdit() }
    );

    if (!needsConfirm) {
      return;
    }

    const timeError = getShiftWindowValidationError(nextStart, nextEnd);
    if (timeError) {
      toast.error(timeError);
      setValue("codeStartTime", committedShiftTimesRef.current.start);
      setValue("codeEndTime", committedShiftTimesRef.current.end);
      return;
    }

    showShiftTimePendingWarning(nextStart, nextEnd);
  };

  const scheduleShiftTimeEvaluation = (nextStart, nextEnd) => {
    pendingShiftTimeRef.current = { nextStart, nextEnd };
    clearShiftTimeDebounce();
    shiftTimeDebounceRef.current = setTimeout(() => {
      shiftTimeDebounceRef.current = null;
      const pending = pendingShiftTimeRef.current;
      if (!pending) return;
      evaluateShiftTimeChange(pending.nextStart, pending.nextEnd);
    }, SHIFT_TIME_CONFIRM_DELAY_MS);
  };

  const handleCodeShiftTimeChange = (field, value) => {
    const normValue = normalizeClockTimeInput(value);
    setValue(field, normValue, { shouldDirty: true });

    const nextStart =
      field === "codeStartTime"
        ? normValue
        : normalizeClockTimeInput(watchFormData?.codeStartTime);
    const nextEnd =
      field === "codeEndTime"
        ? normValue
        : normalizeClockTimeInput(watchFormData?.codeEndTime);

    if (!nextStart || !nextEnd) {
      clearShiftTimePendingWarning();
      return;
    }

    const activeRows = resolveBreakdownRowsForShiftEdit();
    const needsConfirm = shouldConfirmShiftTimeChange(
      nextStart,
      nextEnd,
      committedShiftTimesRef.current,
      activeRows,
      { hasBillingSegments: hasBillingSegmentsForShiftEdit() }
    );

    if (!needsConfirm) {
      clearShiftTimeDebounce();
      clearShiftTimePendingWarning();
      const timeError = getShiftWindowValidationError(nextStart, nextEnd);
      if (timeError) {
        toast.error(timeError);
        setValue("codeStartTime", committedShiftTimesRef.current.start);
        setValue("codeEndTime", committedShiftTimesRef.current.end);
        return;
      }
      applyCodeShiftTimeChange(nextStart, nextEnd, { collapseSplits: false });
      return;
    }

    if (shiftTimePendingWarningRef.current) {
      clearShiftTimeDebounce();
      showShiftTimePendingWarning(nextStart, nextEnd);
      return;
    }

    scheduleShiftTimeEvaluation(nextStart, nextEnd);
  };

  const handleShiftTimePendingCancel = () => {
    clearShiftTimeDebounce();
    setValue("codeStartTime", committedShiftTimesRef.current.start);
    setValue("codeEndTime", committedShiftTimesRef.current.end);
    clearShiftTimePendingWarning();
  };

  const handleShiftTimePendingApply = () => {
    const pending =
      pendingShiftTimeRef.current ||
      pendingShiftTimeChange || {
        nextStart: normalizeClockTimeInput(watchFormData?.codeStartTime),
        nextEnd: normalizeClockTimeInput(watchFormData?.codeEndTime),
      };

    const { nextStart, nextEnd } = pending;
    const timeError = getShiftWindowValidationError(nextStart, nextEnd);
    if (timeError) {
      toast.error(timeError);
      return;
    }

    clearShiftTimeDebounce();
    // Same gate as applyCodeShiftTimeChange: DEN auto-breakdown only.
    // Legacy basic split collapses like Advance.
    const basicAutoBreakdown =
      !isV5 &&
      !legacySplitShift &&
      Boolean(watchFormData?.invoice_breakdown);
    const collapseSplits =
      !basicAutoBreakdown && hasBillingSegmentsForShiftEdit();
    applyCodeShiftTimeChange(nextStart, nextEnd, { collapseSplits });
    clearShiftTimePendingWarning();
    toast.success(
      basicAutoBreakdown
        ? "Shift time updated — day/evening/night billing breakdown will regenerate from the new window."
        : "Shift time updated — worker payroll times and participant billing breakdowns were reset to the new shift window."
    );
  };

  const onSubmit = (data) => {
    if (shiftTimeChanged && !updateConfirmedRef.current) {
      pendingSubmitDataRef.current = data;
      setShowUpdateWarning(true);
      return;
    }
    updateConfirmedRef.current = false;

    if (isV5) {
      const breakdownRows =
        splitShiftBreakdownData?.length > 0
          ? splitShiftBreakdownData
          : timesheetBreakdown;
      const sleepoverValidation = validateAdvanceSleepoverPerParticipant(
        shiftDetails,
        breakdownRows ?? [],
        watchFormData
      );
      if (sleepoverValidation !== true) {
        toast.error(sleepoverValidation);
        return;
      }

      const optedInTravel = (participantTravelRows || []).filter(
        (row) => row?.apply_km ?? row?.km_for_invoicing
      );
      const missingCode = optedInTravel.find(
        (row) =>
          row?.km != null &&
          row.km !== "" &&
          parseFloat(row.km) > 0 &&
          row?.km_item_id == null
      );
      if (missingCode) {
        toast.error(
          "Select a Travel Line Code for each opted-in participant with KM entered."
        );
        return;
      }
      const missingKm = optedInTravel.find(
        (row) => row?.km == null || row.km === ""
      );
      if (missingKm) {
        toast.warning(
          "Some opted-in participants have no KM entered. Travel will not be billed for those participants."
        );
      }
    }

    setSubmitLoader(true);
    const invoiceBreakdownData = [];
    const invoicePrice =
      data?.invoice_ndis_rate !== "alternate"
        ? data?.invoice_ndis_rate
        : data?.rate_amount;

    const codeStart = data?.codeStartTime ?? extractTimeFromDateTime(startTime);
    const codeEnd = data?.codeEndTime ?? extractTimeFromDateTime(endTime);
    const { startDt: scheduleStartDt, endDt: scheduleEndDt } =
      buildShiftWindowDatetimes(shiftDetails, codeStart, codeEnd, schedule_time);
    const pendingSplitsPayload = pendingV5SplitsPayload.current;
    const codeStartNorm = normalizeClockTimeInput(codeStart);
    const codeEndNorm = normalizeClockTimeInput(codeEnd);
    const billingWindowChanged =
      billingWindowChangedRef.current ||
      codeStartNorm !== normalizeClockTimeInput(committedShiftTimesRef.current.start) ||
      codeEndNorm !== normalizeClockTimeInput(committedShiftTimesRef.current.end);

    if (watchFormData?.invoice_breakdown || isV5 || legacySplitShift) {
   
      const breakdownData = splitBreakdownActive
        ? splitShiftBreakdownData
        : timesheetBreakdown?.length > 0
        ? timesheetBreakdown
        : billingLines;
      // Segment dates roll forward per participant. Rows are ordered chronologically
      // and interleaved across participants, so a single shared ratchet pushes one
      // participant's post-midnight segments onto the next participant's day.
      const advanceSegmentBaseDateByGroup = new Map();
      const advancePreviousOvernightByGroup = new Map();
      const breakdownGroupKeyByIndex = new Map();
      groupBreakdownIndicesByParticipant(shiftDetails, breakdownData).forEach(
        (group) => {
          group.indices.forEach((index) => {
            breakdownGroupKeyByIndex.set(index, group.participantKey);
          });
        }
      );
      for (let i = 0; i < breakdownData?.length; i++) {

          let start_time;
          let end_time;
          let duration;

          const segmentGroupKey = breakdownGroupKeyByIndex.get(i) ?? "0";
          let advanceSegmentBaseDate =
            advanceSegmentBaseDateByGroup.get(segmentGroupKey) ?? null;
          let advancePreviousOvernight =
            advancePreviousOvernightByGroup.get(segmentGroupKey) ?? false;

          const breakdownSegmentCount = breakdownData?.length ?? 0;
          const invoiceBreakdownEnabled = Boolean(
            watchFormData?.invoice_breakdown
          );
          const useShiftWindowForLine =
            shouldUseWorkerWindowForBillingLine({
              scheduleTime: schedule_time,
              billingWindowChanged,
              pendingSplitsPayload,
              isAdvanceMode: isV5,
              invoiceBreakdownEnabled,
              breakdownSegmentCount,
            }) ||
            shouldUseScheduleWindowForBillingLine({
              scheduleTime: schedule_time,
              billingWindowChanged,
              pendingSplitsPayload,
              isAdvanceMode: isV5,
              invoiceBreakdownEnabled,
              breakdownSegmentCount,
            });

          if (useShiftWindowForLine) {
            start_time = scheduleStartDt;
            end_time = scheduleEndDt;
            duration = calculateDuration(start_time, end_time);
          } else if (isV5) {
            const row = breakdownData[i];
            const formStart = data[`start_time_${i}`];
            const formEnd = data[`end_time_${i}`];

            // Prefer form clocks so editable breakdown times persist on save.
            if (formStart && formEnd) {
              const segmentTimes = buildAdvanceSegmentBillingDatetimes(
                row,
                formStart,
                formEnd,
                {
                  shiftAnchorDateTime: startTime,
                  previousSegmentOvernight: advancePreviousOvernight,
                  previousBaseDate: advanceSegmentBaseDate,
                }
              );
              start_time = segmentTimes.start_time;
              end_time = segmentTimes.end_time;
              duration = segmentTimes.duration;
              advancePreviousOvernight = segmentTimes.isOvernight;
              advanceSegmentBaseDate = segmentTimes.baseDate;
            } else {
              const storedStart =
                row?.billingSplitMeta?.start_time ?? row?.start_time ?? null;
              const storedEnd =
                row?.billingSplitMeta?.end_time ?? row?.end_time ?? null;

              if (storedStart && storedEnd) {
                start_time = storedStart;
                end_time = storedEnd;
                duration = calculateDuration(storedStart, storedEnd);
                advancePreviousOvernight =
                  isCheckNextDayShift(
                    storedStart,
                    extractTimeFromDateTime(storedStart),
                    extractTimeFromDateTime(storedEnd)
                  ) !== false;
                advanceSegmentBaseDate = moment(storedStart).format("YYYY-MM-DD");
              } else {
                const segmentTimes = buildAdvanceSegmentBillingDatetimes(
                  row,
                  formStart,
                  formEnd,
                  {
                    shiftAnchorDateTime: startTime,
                    previousSegmentOvernight: advancePreviousOvernight,
                    previousBaseDate: advanceSegmentBaseDate,
                  }
                );
                start_time = segmentTimes.start_time;
                end_time = segmentTimes.end_time;
                duration = segmentTimes.duration;
                advancePreviousOvernight = segmentTimes.isOvernight;
                advanceSegmentBaseDate = segmentTimes.baseDate;
              }
            }
          } else {
            // Basic auto-breakdown: never rebuild overnight segments with the
            // legacy useNextDay/baseDate loop — it collapses every row to the
            // full shift window (4 × 14h = 56h). Prefer preview segment
            // datetimes, or the overnight-aware builder used for advance mode.
            const row = breakdownData[i] ?? {};
            const formStart = data[`start_time_${i}`];
            const formEnd = data[`end_time_${i}`];
            const formStartNorm = normalizeClockTimeInput(formStart);
            const formEndNorm = normalizeClockTimeInput(formEnd);
            const formLooksLikeShiftWindow =
              Boolean(formStartNorm) &&
              Boolean(formEndNorm) &&
              formStartNorm === codeStartNorm &&
              formEndNorm === codeEndNorm;
            const rowStart = row?.start_time;
            const rowEnd = row?.end_time;
            const rowStartNorm = normalizeClockTimeInput(
              extractTimeFromDateTime(rowStart)
            );
            const rowEndNorm = normalizeClockTimeInput(
              extractTimeFromDateTime(rowEnd)
            );
            const rowLooksLikeSegment =
              Boolean(rowStartNorm) &&
              Boolean(rowEndNorm) &&
              (rowStartNorm !== codeStartNorm || rowEndNorm !== codeEndNorm);

            if (rowStart && rowEnd && formLooksLikeShiftWindow && rowLooksLikeSegment) {
              start_time = rowStart;
              end_time = rowEnd;
              duration =
                row?.duration || calculateDuration(rowStart, rowEnd);
              advancePreviousOvernight =
                isCheckNextDayShift(
                  rowStart,
                  rowStartNorm,
                  rowEndNorm
                ) !== false;
              advanceSegmentBaseDate = moment(rowStart).format("YYYY-MM-DD");
            } else if (formStart && formEnd) {
              const segmentTimes = buildAdvanceSegmentBillingDatetimes(
                row,
                formStart,
                formEnd,
                {
                  shiftAnchorDateTime: startTime,
                  previousSegmentOvernight: advancePreviousOvernight,
                  previousBaseDate: advanceSegmentBaseDate,
                }
              );
              start_time = segmentTimes.start_time;
              end_time = segmentTimes.end_time;
              duration = segmentTimes.duration;
              advancePreviousOvernight = segmentTimes.isOvernight;
              advanceSegmentBaseDate = segmentTimes.baseDate;
            } else if (rowStart && rowEnd) {
              start_time = rowStart;
              end_time = rowEnd;
              duration =
                row?.duration || calculateDuration(rowStart, rowEnd);
              advancePreviousOvernight =
                isCheckNextDayShift(
                  rowStart,
                  rowStartNorm,
                  rowEndNorm
                ) !== false;
              advanceSegmentBaseDate = moment(rowStart).format("YYYY-MM-DD");
            } else {
              start_time = scheduleStartDt;
              end_time = scheduleEndDt;
              duration = calculateDuration(start_time, end_time);
            }
          }

          advanceSegmentBaseDateByGroup.set(
            segmentGroupKey,
            advanceSegmentBaseDate
          );
          advancePreviousOvernightByGroup.set(
            segmentGroupKey,
            advancePreviousOvernight
          );

        const invoiceItemId = watchFormData[`invoice_item_id_${i}`];
        const breakdownCustomUuid =
          breakdownData[i]?.custom_line_item_uuid ??
          breakdownData[i]?.custom_line_item?.uuid ??
          null;
        const uniqueCode =
          breakdownData[i]?.ndis_support_item_number ??
          breakdownData[i]?.custom_line_item?.unique_code ??
          "";
        const isCustomPrefix =
          typeof invoiceItemId === "string" &&
          invoiceItemId.startsWith("custom:");
        const matchesUniqueCode =
          invoiceItemId != null &&
          uniqueCode !== "" &&
          String(invoiceItemId) === String(uniqueCode);
        const isOversizedNumericId =
          invoiceItemId != null &&
          invoiceItemId !== "" &&
          !isPostgresIntegerId(invoiceItemId) &&
          Number.isFinite(Number(invoiceItemId));
        const isCustomLineItem =
          isCustomPrefix ||
          !!breakdownCustomUuid ||
          matchesUniqueCode ||
          isOversizedNumericId;
        const customLineItemUuid = isCustomPrefix
          ? String(invoiceItemId).replace(/^custom:/, "")
          : breakdownCustomUuid || null;
        const ndisInvoiceItemId = customLineItemUuid ? null : invoiceItemId;
        const serviceTypeId =
          isV5 || legacySplitShift
            ? watchFormData[`invoice_service_type_id_${i}`] ??
              breakdownData[i]?.service_type_id ??
              breakdownData[i]?.invoice_service_type_id
            : breakdownData[i]?.service_type_id;
        const serviceTypeName =
          isV5 || legacySplitShift
            ? serviceTypes?.find((s) => s.value === serviceTypeId)?.name ??
              breakdownData[i]?.service_type_name ??
              breakdownData[i]?.invoice_service_type_name
            : breakdownData[i]?.service_type_name;

        const invoiceNdisRate = watchFormData[`invoice_ndis_rate_${i}`];
        const shiftType = breakdownData[i]?.shift_type;
        const breakdownRatio =
          watchFormData[`ratio_${i}`] ??
          breakdownData[i]?.ratio ??
          shiftDetails?.ratio ??
          "1:1";
        
        // const duration = breakdownData[i]?.duration
        //   ? breakdownData[i]?.duration
        //   : calculateDuration(
        //       breakdownData[i]?.start_time,
        //       breakdownData[i]?.end_time
        //     );

        // const start_time = breakdownData[i]?.start_time;
        // const end_time = breakdownData[i]?.end_time;

        const sleepover = watchFormData[`sleepover_${i}`];

        const activeHoursDuration = watchFormData[`active_hours_${i}`];

        const activeHoursNDISID =
          watchFormData[`invoice_item_id_active_hours${i}`];

        const activeHoursInvoiceNdisRate =
          watchFormData[`invoice_ndis_rate_active_hours${i}`];

        // Untouched "actual" segments often have empty RHF rate fields while the
        // UI still shows catalogue from the row — never persist NaN/0 for those.
        const resolvedUnitRate =
          resolveSubmitBreakdownUnitRate({
            invoiceNdisRate,
            rateAmount: watchFormData[`rate_amount_${i}`],
            breakdownRow: breakdownData[i],
          }) ?? 0;

        if (invoiceNdisRate === "alternate") {
          invoiceBreakdownData.push(
            enrichInvoiceLinePayload(
              {
                invoice_item_id: ndisInvoiceItemId,
                ...(customLineItemUuid ? { custom_line_item_uuid: customLineItemUuid } : {}),
                invoice_service_type_id: serviceTypeId,
                service_type_name: serviceTypeName,
                ndis_rate: resolvedUnitRate,
                invoice_price: resolvedUnitRate,
                shift_type: shiftType,
                ratio: breakdownRatio,
                duration,
                start_time,
                end_time,
                alternate_rate: true,
                sleepover,
                is_active_hours:
                  sleepover &&
                  activeHoursNDISID &&
                  activeHoursDuration !== "" &&
                  activeHoursDuration !== undefined
                    ? true
                    : false,
              },
              breakdownData[i],
              shiftDetails,
              i
            )
          );
        } else {
          invoiceBreakdownData.push(
            enrichInvoiceLinePayload(
              {
                invoice_item_id: ndisInvoiceItemId,
                ...(customLineItemUuid ? { custom_line_item_uuid: customLineItemUuid } : {}),
                invoice_service_type_id: serviceTypeId,
                service_type_name: serviceTypeName,
                ndis_rate: resolvedUnitRate,
                invoice_price: resolvedUnitRate,
                shift_type: shiftType,
                ratio: breakdownRatio,
                duration,
                start_time,
                end_time,
                alternate_rate: false,
                sleepover,
                is_active_hours:
                  sleepover &&
                  activeHoursNDISID &&
                  activeHoursDuration !== "" &&
                  activeHoursDuration !== undefined
                    ? true
                    : false,
              },
              breakdownData[i],
              shiftDetails,
              i
            )
          );
        }
        if (
          sleepover &&
          activeHoursNDISID &&
          activeHoursDuration !== "" &&
          activeHoursDuration !== undefined
        ) {
          if (activeHoursInvoiceNdisRate === "alternate") {
            const rateAmountActiveHours =
              watchFormData[`rate_amount_active_hours${i}`];

            invoiceBreakdownData.push(
              enrichInvoiceLinePayload(
                {
                  invoice_item_id: activeHoursNDISID,
                  ndis_rate: parseFloat(rateAmountActiveHours),
                  invoice_price: parseFloat(rateAmountActiveHours),
                  shift_type: shiftType,
                  duration: activeHoursDuration,
                  start_time,
                  end_time,
                  alternate_rate: true,
                },
                breakdownData[i]?.children?.[0] ?? {},
                shiftDetails,
                i,
                { attachParentUuid: false }
              )
            );
          } else {
            invoiceBreakdownData.push(
              enrichInvoiceLinePayload(
                {
                  invoice_item_id: activeHoursNDISID,
                  ndis_rate: parseFloat(activeHoursInvoiceNdisRate),
                  invoice_price: parseFloat(activeHoursInvoiceNdisRate),
                  shift_type: shiftType,
                  duration: activeHoursDuration,
                  start_time,
                  end_time,
                  alternate_rate: false,
                },
                breakdownData[i]?.children?.[0] ?? {},
                shiftDetails,
                i,
                { attachParentUuid: false }
              )
            );
          }
        }
      }

      // Drop stray DEN/preview rows (no code and no split). Real segments keep
      // split_shift_id so a missing code still fails validation on the backend.
      if (legacySplitShift && invoiceBreakdownData.length > 0) {
        const kept = invoiceBreakdownData.filter(
          (line) =>
            line?.invoice_item_id ||
            line?.custom_line_item_uuid ||
            line?.split_shift_id
        );
        invoiceBreakdownData.length = 0;
        invoiceBreakdownData.push(...kept);
      }

      // Window collapse / replace-splits: omit stale ids so BE creates fresh splits.
      // Segment-only edits keep split_shift_id so schedule syncOrCreate can match.
      if (pendingSplitsPayload?.splits?.length || billingWindowChanged) {
        invoiceBreakdownData.forEach((line) => {
          delete line.uuid;
          delete line.split_shift_id;
        });
      }
    }

    // V5: chronological order before POST (BE also sorts; keeps sleepover pairs intact).
    if (isV5 && invoiceBreakdownData.length > 0) {
      const sorted = sortBillingRowsByStartTime(invoiceBreakdownData);
      invoiceBreakdownData.length = 0;
      invoiceBreakdownData.push(...sorted);
    }

    if (data?.non_billable) {
      invoiceBreakdownData.forEach((line) => {
        line.ndis_rate = 0;
        line.invoice_price = 0;
      });
    }

    const startDt = scheduleStartDt;
    const endDt = scheduleEndDt;

    const payload = {
      schedule_time,
      invoice_non_billable: Boolean(data?.non_billable),
      default_timesheet_breakdown: watchFormData?.invoice_breakdown,
      invoice_cancelled: Boolean(data?.cancelled),
  
      state_id: isV5 ? shiftDetails?.state_id : catalogState?.value,
      sleepover: isV5
        ? invoiceBreakdownData.some((row) => parseBooleanVal(row?.sleepover))
        : parseBooleanVal(data?.sleepover),
      invoice_service_type_id: isV5
        ? watchFormData[`invoice_service_type_id_0`] ?? shiftDetails?.invoice_service_type_id
        : serviceCategory?.value,
      invoice_category_name: isV5
        ? shiftDetails?.invoice_category_name
        : categoryItemName?.value,
      shift_date: moment
        .utc(moment(shiftDetails?.time_sheet_date).format("YYYY-MM-DD"))
        .format("YYYY-MM-DDT00:00:00.000[Z]"),
      timesheet_length: isV5
        ? getBasicShiftDurationHHMM(codeStart, codeEnd) ||
          getShiftLength(codeStart, codeEnd, true)
        : getShiftLength(codeStart, codeEnd, true),
    };
    if (schedule_time) {
      Object.assign(payload, {
        start_time: startDt,
        end_time: endDt,
        worker_start_time: shiftDetails?.worker_start_time,
        worker_end_time: shiftDetails?.worker_end_time,
      });
      if (billingWindowChanged) {
        payload.schedule_window_changed = true;
      }
    } else {
      Object.assign(payload, {
        worker_start_time: startDt,
        worker_end_time: endDt,
      });
      if (billingWindowChanged) {
        payload.worker_window_changed = true;
      }
    }
    if (isV5 || legacySplitShift) {
      payload.default_timesheet_breakdown = true;
    }
    if (watchFormData?.invoice_breakdown || isV5 || legacySplitShift) {
      Object.assign(payload, {
        invoice_timesheets: invoiceBreakdownData,
      });
    }
    if (isV5) {
      const travelPayload = buildParticipantTravelSavePayload(
        participantTravelRows
      );
      if (travelPayload.length > 0) {
        Object.assign(payload, {
          participant_travel: travelPayload,
        });
      }
    } else {
      // Legacy basic / basic-split Travel & KM (timesheet columns) — always post
      // from Code Allocation, including invoicing CA (hidePayrollFields).
      const kmRaw = watchFormData?.km;
      const kmValue =
        kmRaw === null || kmRaw === undefined || kmRaw === ""
          ? 0
          : kmRaw;
      const applyKm = parseBooleanVal(watchFormData?.applykm);
      Object.assign(payload, {
        km: kmValue,
        apply_km: applyKm,
      });
      if (applyKm && watchFormData?.km_item_id) {
        Object.assign(payload, {
          km_item_id: watchFormData.km_item_id,
        });
      }
      if (applyKm && ndisitemRate?.value === "alternate") {
        Object.assign(payload, {
          km_alternative_rate: true,
          km_alternative_price:
            watchFormData?.km_alternate_nids_rate_amount,
        });
      } else if (applyKm) {
        Object.assign(payload, {
          km_alternative_rate: false,
        });
      }
    }

    if (!watchFormData?.invoice_breakdown && !legacySplitShift) {
      const singleItemId = data?.invoice_item_id;
      const isCustomSingle =
        typeof singleItemId === "string" && singleItemId?.startsWith("custom:");
      const singleCustomUuid = isCustomSingle
        ? String(singleItemId).replace(/^custom:/, "")
        : null;
      Object.assign(payload, {
        invoice_item_id: isCustomSingle ? null : singleItemId,
        invoice_price: data?.non_billable ? 0 : invoicePrice,
        invoice_ndis_rate: data?.non_billable ? 0 : data?.invoice_ndis_rate,
        ...(singleCustomUuid ? { custom_line_item_uuid: singleCustomUuid } : {}),
      });
    }
  

    if (
      roleAccess([USER_ROLE.Sp_Admin]) &&
      currentSpUser &&
      currentSpUser?.id
    ) {
      Object.assign(payload, {
        updated_by_user: currentSpUser.id,
      });
    }

    if (roleAccess([USER_ROLE.Sp_manager]) && currentSpUser) {
      Object.assign(payload, {
        updated_by_user:
          currentSpUser?.sp_id || currentSpUser?.service_provider?.id,
      });
    }
    if (shiftDetails && shiftDetails?.worker_id) {
      Object.assign(payload, {
        worker_id: shiftDetails.worker_id,
      });
    }
    const saveUrl = useShiftBillingApi
      ? URL_V5_SHIFT_BILLING_ALLOCATION(shiftBillingUuid)
      : `${URL_ROSTERING_TIMESHEET_UPDATE_CODE_ALLOCATION}/${shiftDetails?.uuid}`;

    const persistBilling = () =>
      apiPostRequest(saveUrl, payload).then((res) => {
        if (res.status === 200) {
          const updated = parseShiftBillingSaveResponse(res);
          setShiftDetails(updated);
          if (isV5) {
            setParticipantTravelRows(
              hydrateParticipantTravelRows(getParticipantTravelRows(updated))
            );
          }
          const savedUuid = updated?.uuid;
          const currentUuid = shiftDetails?.uuid;
          if (
            !savedUuid ||
            !currentUuid ||
            String(savedUuid) === String(currentUuid)
          ) {
            syncTimesheetTableRowFromDetail(dispatch, updated);
          }
          originalShiftStateRef.current = null;
          shiftTimeLocallyEditedRef.current = false;
          updateConfirmedRef.current = false;
          setShiftTimeChanged(false);
          setShowUpdateWarning(false);

          if (useShiftBillingApi || isV5) {
            // Re-enable saved-line overlay after a successful persist.
            suppressSavedBillingOverlayRef.current = false;
            const { lines, committed } = getEditorStateFromBillingSave(
              updated,
              schedule_time
            );
            if (lines.length > 0) {
              setTimesheetBreakdown(lines);
              breakdownSegmentCountRef.current = lines.length;
              syncBreakdownFormAfterBillingSave(updated, lines, setValue);
            }
            committedShiftTimesRef.current = committed;
            billingWindowChangedRef.current = false;
            setValue("codeStartTime", committed.start);
            setValue("codeEndTime", committed.end);
            pendingV5SplitsPayload.current = null;
            isFirstAnyValueChanged.current = false;
          }

          toast.success(
            useShiftBillingApi || isV5
              ? "Code allocation updated — billing applies to the whole shift."
              : "Code allocation updated successfully"
          );
          if (typeof onUpdateSuccess === "function") {
            onUpdateSuccess();
          }

          // Warm schedule/advance show so calendar edit form is not stale after split sync.
          const advanceShiftUuid =
            getShiftUuidForBilling(updated) ?? shiftBillingUuid;
          if ((useShiftBillingApi || isV5) && advanceShiftUuid) {
            apiGetRequest(
              `${URL_ROSTERING_SHIFT_V5}/${advanceShiftUuid}`
            ).catch(() => {});
          }
        }
      });

    const splitsPayload = pendingV5SplitsPayload.current;
    const shiftId = shiftDetails?.shift_id;

    const savePromise =
      splitsPayload?.splits?.length && shiftId
        ? apiPostRequest(URL_V5_SHIFT_SPLITS(shiftId), splitsPayload).then(() => {
            pendingV5SplitsPayload.current = null;
            return persistBilling();
          })
        : persistBilling();

    savePromise
      .catch((error) => {
        console.error("Error updating timesheet:", error);
        toast.error(error.message || "Error updating timesheet");
      })
      .finally(() => {
        setSubmitLoader(false);
      });
  };

  const handleConfirmUpdate = () => {
    setShowUpdateWarning(false);
    updateConfirmedRef.current = true;
    const data = pendingSubmitDataRef.current;
    pendingSubmitDataRef.current = null;
    if (data) {
      onSubmit(data);
    }
  };

  const handleCancelUpdate = () => {
    setShowUpdateWarning(false);
    pendingSubmitDataRef.current = null;
  };

const [ndisitemRate, setNdisItemRate] = useState(null);
  const NDISRateOptions = useMemo(() => {

    return [
      {
        label:"Apply Alternate Rate",
        value:"alternate"
      },
      {
        label:travelNdisNum?.price,
        value:travelNdisNum?.price,
      }
    ]
  },[travelNdisNum])
 

  useEffect(() => {
    if(travelNdisNum?.km_alternative_rate){
   setNdisItemRate(NDISRateOptions[0]);
   setValue("km_alternate_nids_rate_amount",travelNdisNum?.km_alternative_price)
    }else if (travelNdisNum?.value && NDISRateOptions) {
      const selectedNdisObj = NDISRateOptions?.find(
        (item) => item.value === travelNdisNum?.price
      );
      setNdisItemRate(selectedNdisObj);

    }
  }, [travelNdisNum,NDISRateOptions]);
  
  const shiftDurationLabel = useMemo(() => {
    const codeStart = normalizeClockTimeInput(watchFormData?.codeStartTime);
    const codeEnd = normalizeClockTimeInput(watchFormData?.codeEndTime);
    const shiftSpansNextCalendarDay = shiftClocksSpanNextCalendarDay(
      startTime,
      endTime
    );
    const resolveClockDuration = (start, end) => {
      if (!start || !end) return null;
      // Advance/V5: same clock start/end is a 24h window (getShiftLength → "00:00").
      const windowDuration = isV5 ? getBasicShiftDurationHHMM(start, end) : null;
      if (windowDuration) return windowDuration;

      return resolveBasicShiftDurationHHMM(start, end, {
        spansNextCalendarDay: shiftSpansNextCalendarDay,
      });
    };
    if (codeStart && codeEnd) {
      return resolveClockDuration(codeStart, codeEnd);
    }
    if (!startTime || !endTime) return null;
    return schedule_time
      ? resolveClockDuration(
          extractTimeFromDateTime(shiftDetails?.start_time),
          extractTimeFromDateTime(shiftDetails?.end_time)
        )
      : resolveClockDuration(
          extractTimeFromDateTime(shiftDetails?.worker_start_time),
          extractTimeFromDateTime(shiftDetails?.worker_end_time)
        );
  }, [
    watchFormData?.codeStartTime,
    watchFormData?.codeEndTime,
    startTime,
    endTime,
    schedule_time,
    shiftDetails?.start_time,
    shiftDetails?.end_time,
    shiftDetails?.worker_start_time,
    shiftDetails?.worker_end_time,
    isV5,
  ]);

  const catalogueRate =
    ndisItemName?.price ?? watchFormData?.rate_amount ?? null;
  const billRate = watchFormData?.rate_amount ?? ndisItemName?.price ?? null;
  const appliedRate =
    billRate && shiftDetails?.participants?.length
      ? Number(billRate) / shiftDetails.participants.length
      : billRate;

  const activeBreakdownRows = useMemo(() => {
    if (showSplitAccordion && splitShiftBreakdownData.length > 0) {
      return splitShiftBreakdownData;
    }
    if (
      !isV5 &&
      watchFormData?.invoice_breakdown &&
      timesheetBreakdown?.length > 0
    ) {
      return timesheetBreakdown;
    }
    return [];
  }, [
    showSplitAccordion,
    splitShiftBreakdownData,
    isV5,
    watchFormData?.invoice_breakdown,
    timesheetBreakdown,
  ]);

  const invoiceEstimateDisplay = useMemo(() => {
    if (watchFormData?.non_billable) return "$0.00";

    const total = calculateCodeAllocationInvoiceEstimate({
      watchFormData,
      shiftDetails,
      timeDisplayPreference,
      timesheetBreakdown:
        activeBreakdownRows.length > 0
          ? activeBreakdownRows
          : timesheetBreakdown,
      travelNdisNum,
      ndisitemRate,
      ndisItemName,
      startTime,
    });

    return formatCodeAllocationInvoiceEstimate(total) ?? "—";
  }, [
    watchFormData,
    shiftDetails,
    timeDisplayPreference,
    activeBreakdownRows,
    timesheetBreakdown,
    travelNdisNum,
    ndisitemRate,
    ndisItemName,
    startTime,
  ]);

  const participantBreakdownGroups = useMemo(
    () => groupBreakdownIndicesByParticipant(shiftDetails, activeBreakdownRows),
    [shiftDetails, activeBreakdownRows]
  );
  const billingParticipantCount = useMemo(
    () =>
      countUniqueBillingParticipants(
        participantBreakdownGroups,
        shiftDetails
      ),
    [participantBreakdownGroups, shiftDetails]
  );

  const isShiftSta = useMemo(() => {
    const raw = shiftDetails?.is_sta ?? shiftDetails?.sta;
    return parseBooleanVal(raw) || raw === 1 || String(raw) === "1";
  }, [shiftDetails?.is_sta, shiftDetails?.sta]);

  // Advance-only: never show for basic, basic-split (legacy), DEN breakdowns, or STA.
  const canSplitBreakdownSegments =
    !billingDisabled &&
    !isShiftSta &&
    !legacySplitShift &&
    (shiftDetails?.billing_mode
      ? shiftDetails.billing_mode === "advance"
      : isV5);

  const resolveEditableBreakdownRows = useCallback(() => {
    if (showSplitAccordion && splitShiftBreakdownData?.length > 0) {
      return splitShiftBreakdownData;
    }
    if (timesheetBreakdown?.length > 0) {
      return timesheetBreakdown;
    }
    return buildSplitBreakdownRows(shiftDetails, timesheetBreakdown) ?? [];
  }, [
    showSplitAccordion,
    splitShiftBreakdownData,
    timesheetBreakdown,
    shiftDetails,
  ]);

  const persistLocalBreakdownRows = useCallback(
    (nextRows) => {
      setTimesheetBreakdown(nextRows);
      breakdownSegmentCountRef.current = nextRows.length;
      isFirstAnyValueChanged.current = false;
      if (isV5 && typeof setShiftDetails === "function") {
        setShiftDetails((prev) =>
          applyLocalBreakdownRowsToShiftDetails(prev, nextRows)
        );
      }
    },
    [isV5, setShiftDetails]
  );

  const handleSplitBreakdownSegment = useCallback(
    (index) => {
      if (!canSplitBreakdownSegments) return;

      const rows = resolveEditableBreakdownRows();
      if (!Array.isArray(rows) || !rows[index]) return;

      if (rows.length >= CODE_ALLOCATION_MAX_BREAKDOWN_SPLITS) {
        toast.warning(
          `Maximum of ${CODE_ALLOCATION_MAX_BREAKDOWN_SPLITS} participant breakdowns allowed`,
          { toastId: TOAST_IDS.MAX_SPLIT_SHIFT_LIMIT }
        );
        return;
      }

      const insertAt = index + 1;
      const cloned = cloneBreakdownRowForSegmentSplit(
        rows[index],
        shiftDetails,
        index
      );
      const formServiceTypeId = getValues(`invoice_service_type_id_${index}`);
      const formInvoiceItemId = getValues(`invoice_item_id_${index}`);
      const formRatio = getValues(`ratio_${index}`);
      if (formServiceTypeId != null && formServiceTypeId !== "") {
        cloned.service_type_id = formServiceTypeId;
        cloned.invoice_service_type_id = formServiceTypeId;
      }
      if (formInvoiceItemId != null && formInvoiceItemId !== "") {
        cloned.invoice_item_id = formInvoiceItemId;
      }
      if (formRatio != null && formRatio !== "") {
        cloned.ratio = formRatio;
      }
      const nextRows = [...rows];
      nextRows.splice(insertAt, 0, cloned);

      shiftBreakdownFormFieldsAfterInsert(
        getValues,
        setValue,
        insertAt,
        rows.length
      );
      copyBreakdownFormSegment(getValues, setValue, index, insertAt, {
        clearSleepover: true,
      });

      persistLocalBreakdownRows(nextRows);
    },
    [
      canSplitBreakdownSegments,
      resolveEditableBreakdownRows,
      shiftDetails,
      getValues,
      setValue,
      persistLocalBreakdownRows,
    ]
  );

  const handleRemoveBreakdownSegment = useCallback(
    (index) => {
      if (!canSplitBreakdownSegments) return;

      const rows = resolveEditableBreakdownRows();
      if (!Array.isArray(rows) || !rows[index]) return;

      const participantKey = getBreakdownParticipantKey(
        shiftDetails,
        index,
        rows[index]
      );
      const siblingCount = rows.filter(
        (row, i) =>
          getBreakdownParticipantKey(shiftDetails, i, row) === participantKey
      ).length;

      if (siblingCount <= 1) {
        toast.warning("At least one breakdown is required per participant", {
          toastId: "code-allocation-min-breakdown",
        });
        return;
      }

      const nextRows = rows.filter((_, i) => i !== index);
      shiftBreakdownFormFieldsAfterRemove(
        getValues,
        setValue,
        index,
        rows.length
      );
      persistLocalBreakdownRows(nextRows);
    },
    [
      canSplitBreakdownSegments,
      resolveEditableBreakdownRows,
      shiftDetails,
      getValues,
      setValue,
      persistLocalBreakdownRows,
    ]
  );

  const getBreakdownRemovePermission = useCallback(
    (index) => {
      if (!canSplitBreakdownSegments) return false;
      const rows = activeBreakdownRows;
      if (!rows?.length || !rows[index]) return false;
      const participantKey = getBreakdownParticipantKey(
        shiftDetails,
        index,
        rows[index]
      );
      return (
        rows.filter(
          (row, i) =>
            getBreakdownParticipantKey(shiftDetails, i, row) === participantKey
        ).length > 1
      );
    },
    [canSplitBreakdownSegments, activeBreakdownRows, shiftDetails]
  );

  const renderSplitAccordionItem = (item, index, displayCtx = {}) => (
    <Accordion
      key={`split-bd-${item?._clientKey ?? item?.id ?? item?.uuid ?? index}-${index}`}
      timesheetBreakdown={
        activeBreakdownRows?.length > 0 ? activeBreakdownRows : timesheetBreakdown
      }
      setValue={setValue}
      control={control}
      isDisabled={billingDisabled}
      item={item}
      startTime={startTime}
      endTime={endTime}
      disabledAllComponent={!isTimesheetServiceCatNonSTA}
      index={index}
      register={register}
      watchFormData={watchFormData}
      shiftDetails={shiftDetails}
      billingSplitMeta={
        item?.billingSplitMeta ?? getBillingSplitMeta(shiftDetails, index, item)
      }
      isSplitBillingMode={legacySplitShift}
      isAdvanceMode={isV5}
      serviceTypes={serviceTypes}
      setCatalogState={setCatalogState}
      catalogState={catalogState}
      serviceCategory={serviceCategory}
      serviceCategoryId={serviceCategory?.value}
      isNonBillable={watchFormData?.non_billable}
      suggestedItemNumber={
        isV5
          ? getSplitSuggestedItem(item)
          : splitShiftBreakdownData?.length > 0 &&
            isFirstAnyValueChanged.current === true
          ? getSplitSuggestedItem(item)
          : item?.item_numbers?.selected
      }
      itemNumber={
        accordionItemNumbersByKey.get(
          getAccordionItemNumberKey(item, index)
        ) ?? EMPTY_ITEM_NUMBERS
      }
      errors={errors}
      totalBreakDown={
        activeBreakdownRows?.length ||
        splitShiftBreakdownData?.length ||
        0
      }
      totalItemsDuration={totalItemsDuration}
      spSettings={spSettings}
      clearErrors={clearErrors}
      isSingleTimesheetBreakdowns={isFirstAnyValueChanged.current === true}
      showClaimTypeFromBusinessSettings={showClaimTypeFromBusinessSettings}
      showTravelFromBusinessSettings={showTravelFromBusinessSettings}
      localBreakdownIndex={displayCtx.localIndex}
      localBreakdownTotal={displayCtx.localTotal}
      shiftWindowStart={watchFormData?.codeStartTime}
      shiftWindowEnd={watchFormData?.codeEndTime}
      allowSplit={canSplitBreakdownSegments && isV5 && !legacySplitShift}
      canRemove={getBreakdownRemovePermission(index)}
      onSplit={() => handleSplitBreakdownSegment(index)}
      onRemove={() => handleRemoveBreakdownSegment(index)}
    />
  );

  const renderStandardAccordionItem = (item, index, displayCtx = {}) => (
    <Accordion
      key={`bd-${index}-${serviceCategory?.value ?? "svc"}-${item?.shift_type ?? "seg"}`}
      timesheetBreakdown={
        activeBreakdownRows?.length > 0 ? activeBreakdownRows : timesheetBreakdown
      }
      setValue={setValue}
      startTime={startTime}
      endTime={endTime}
      control={control}
      isDisabled={billingDisabled}
      item={item}
      disabledAllComponent={!isTimesheetServiceCatNonSTA}
      index={index}
      register={register}
      watchFormData={watchFormData}
      shiftDetails={shiftDetails}
      billingSplitMeta={getBillingSplitMeta(shiftDetails, index, item)}
      isSplitBillingMode={legacySplitShift}
      setCatalogState={setCatalogState}
      catalogState={catalogState}
      serviceCategory={serviceCategory}
      serviceCategoryId={serviceCategory?.value}
      isNonBillable={watchFormData?.non_billable}
      suggestedItemNumber={
        billingLines?.length > 0 && isFirstAnyValueChanged.current === true
          ? {
              id: item?.invoice_item_id,
              price: item?.invoice_price,
              support_item_name: item?.ndis_support_item_name,
              support_item_number: item?.ndis_support_item_number,
              ...(parseBooleanVal(item?.alternate_rate)
                ? {
                    is_alternate: true,
                    alternate_price: item?.ndis_rate ?? item?.invoice_price,
                  }
                : {}),
            }
          : item?.item_numbers?.selected
      }
      itemNumber={
        accordionItemNumbersByKey.get(
          getAccordionItemNumberKey(item, index)
        ) ?? item?.item_numbers?.items?.map(mapInvoiceItemOption) ?? EMPTY_ITEM_NUMBERS
      }
      errors={errors}
      totalBreakDown={
        activeBreakdownRows?.length || timesheetBreakdown?.length || 0
      }
      totalItemsDuration={totalItemsDuration}
      spSettings={spSettings}
      clearErrors={clearErrors}
      isSingleTimesheetBreakdowns={isFirstAnyValueChanged.current === true}
      showClaimTypeFromBusinessSettings={showClaimTypeFromBusinessSettings}
      showTravelFromBusinessSettings={showTravelFromBusinessSettings}
      localBreakdownIndex={displayCtx.localIndex}
      localBreakdownTotal={displayCtx.localTotal}
      shiftWindowStart={watchFormData?.codeStartTime}
      shiftWindowEnd={watchFormData?.codeEndTime}
      allowSplit={false}
      canRemove={getBreakdownRemovePermission(index)}
      onSplit={() => handleSplitBreakdownSegment(index)}
      onRemove={() => handleRemoveBreakdownSegment(index)}
    />
  );

  const getGroupDurationLabel = (rows, indices) => {
    // Advance/V5: same-clock leftover splits are 0h unless the shift itself is 24h.
    if (isV5) {
      const shiftStart = watchFormData?.codeStartTime;
      const shiftEnd = watchFormData?.codeEndTime;
      const segments = [];
      let usedFormClocks = false;
      indices.forEach((i) => {
        const start = watchFormData?.[`start_time_${i}`];
        const end = watchFormData?.[`end_time_${i}`];
        if (!start || !end) return;
        usedFormClocks = true;
        segments.push({ start, end });
      });
      if (usedFormClocks) {
        const totalMinutes = sumAdvanceSegmentMinutes(
          segments,
          shiftStart,
          shiftEnd
        );
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return formatBillingDurationLabel(
          `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`
        );
      }
    }

    if (legacySplitShift) {
      let totalMinutes = 0;
      let usedFormClocks = false;
      indices.forEach((i) => {
        const start = watchFormData?.[`start_time_${i}`];
        const end = watchFormData?.[`end_time_${i}`];
        if (!start || !end) return;
        usedFormClocks = true;
        const hhmm = getBasicShiftDurationHHMM(start, end);
        if (!hhmm) return;
        const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10) || 0);
        totalMinutes += h * 60 + m;
      });
      if (usedFormClocks) {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return formatBillingDurationLabel(
          `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`
        );
      }
    }

    if (!isV5 && !legacySplitShift) {
      let totalMinutes = 0;
      let usedFormClocks = false;
      indices.forEach((i) => {
        const start = watchFormData?.[`start_time_${i}`];
        const end = watchFormData?.[`end_time_${i}`];
        if (!start || !end) return;
        usedFormClocks = true;
        const hhmm = getBasicShiftDurationHHMM(start, end);
        if (!hhmm) return;
        const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10) || 0);
        totalMinutes += h * 60 + m;
      });
      if (usedFormClocks) {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return formatBillingDurationLabel(
          `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`
        );
      }
    }

    const subset = indices.map((i) => rows[i]).filter(Boolean);
    // If stored durations are all zero but form clocks imply a full day, still show 24h.
    const fromStored = calculateTotalDuration(subset);
    if (
      isV5 &&
      (!fromStored || fromStored.startsWith("00:00")) &&
      indices.length > 0
    ) {
      const first = indices[0];
      const row = rows[first];
      const start =
        watchFormData?.[`start_time_${first}`] ||
        extractTimeFromDateTime(row?.start_time);
      const end =
        watchFormData?.[`end_time_${first}`] ||
        extractTimeFromDateTime(row?.end_time);
      const hhmm = getBasicShiftDurationHHMM(start, end);
      if (hhmm) {
        return formatBillingDurationLabel(`${hhmm}:00`);
      }
    }
    return formatBillingDurationLabel(fromStored);
  };

  const renderParticipantGroupedBreakdowns = (rows, renderItem) => (
    <div className="ca-participant-groups">
      {participantBreakdownGroups.map((group, groupIndex) => (
        <TimesheetCodeAllocationParticipantGroup
          key={group.participantKey}
          participantIndex={groupIndex + 1}
          totalParticipants={participantBreakdownGroups.length}
          initials={group.initials}
          participantLabel={group.participantLabel}
          breakdownCount={group.indices.length}
          durationLabel={getGroupDurationLabel(rows, group.indices)}
          ratio={group.ratio ?? shiftDetails?.ratio}
          defaultExpanded
        >
          {group.indices.map((index, localIdx) => {
            const item = rows[index];
            const displayCtx = {
              localIndex: localIdx + 1,
              localTotal: group.indices.length,
            };
            return (
              <div
                key={`${shiftDetails?.uuid || "ts"}-${
                  item?._clientKey ||
                  item?.uuid ||
                  item?.timesheet_id ||
                  item?.id ||
                  index
                }`}
                className="ca-participant-breakdown-slot"
              >
                {renderItem(item, index, displayCtx)}
              </div>
            );
          })}
        </TimesheetCodeAllocationParticipantGroup>
      ))}
    </div>
  );
  const rateStripValues = useMemo(
    () =>
      resolveCodeAllocationRateStripValues({
        watchFormData,
        shiftDetails,
        ndisItemName,
        timesheetBreakdown,
        breakdownSegmentCount,
        invoiceBreakdownEnabled: Boolean(watchFormData?.invoice_breakdown),
      }),
    [
      watchFormData,
      shiftDetails,
      ndisItemName,
      timesheetBreakdown,
      breakdownSegmentCount,
    ]
  );

  const showRateStrip =
    !shiftDetails?.is_split_shift &&
    (!watchFormData?.invoice_breakdown || breakdownSegmentCount === 1);

  const travelSection = (
    <TimesheetCodeAllocationSectionCard
      title="Travel & KM"
      className="ca-travel-section"
      headerAction={
        !isNaN(watchFormData?.km) &&
        watchFormData?.km &&
        watchFormData?.km !== null &&
        parseBooleanVal(spSettings?.apply_km_to_invoice) ? (
          <div className="ca-apply-km-pill">
            <span>Apply KMs to invoicing</span>
            <Controller
              name="applykm"
              control={control}
              defaultValue={watchFormData?.applykm || false}
              render={({ field }) => (
                <ToggleSwitch
                  {...field}
                  isSmall
                  disabled={billingDisabled}
                  onChange={(value) => {
                    field.onChange(value);
                    setValue(`applykm`, value);
                    if (value) {
                      fetchApplyKmDetails();
                    }
                  }}
                />
              )}
            />
          </div>
        ) : null
      }
    >
      {shiftDetails?.auto_calculated_km && (
        <div className="ca-google-km-row">
          <span className="ca-google-km-label">
            Calculated Travel Distance (via Google API):{" "}
            {shiftDetails?.auto_calculated_km} KM
          </span>
          <div className="ca-google-km-toggle">
            <span>Use This Distance</span>
            <Controller
              name="use_distance"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <ToggleSwitch
                  {...field}
                  isSmall
                  disabled={billingDisabled}
                  onChange={(value) => {
                    field.onChange(value);
                    setValue(
                      "km",
                      value ? shiftDetails?.auto_calculated_km : ""
                    );
                  }}
                />
              )}
            />
          </div>
        </div>
      )}

      <div className="min-w-0">
        <label className="ca-field-label--dark">KM</label>
        <InputField
          cssClass="mt-0 h-40-important text-gray-700"
          inputWidth="w-full"
          setValue={setValue}
          isDisabled={billingDisabled}
          register={register}
          backgroundColor="#F3F4F6"
          readOnly={watchFormData?.applydistance}
          type="text"
          pattern={/^\d*[0-9][.]?\d*$/}
          value={watchFormData?.km}
          name="km"
          ErrorMessage={
            errors?.km?.type === "pattern"
              ? "only number is required!"
              : null
          }
        />
      </div>

      {watchFormData?.applykm && watchFormData?.km ? (
        traveldata !== null ? (
          <div className="ca-travel-subsection">
            <p className="ca-travel-subsection-title">Travel invoicing line</p>
            <div className="min-w-0">
              <label className="ca-field-label">NDIS Item Name</label>
              <InputField
                isDisabled={true}
                cssClass="mt-0 h-40-important text-gray-700"
                inputWidth="w-full"
                backgroundColor="#F3F4F6"
                value={travelNdisNum?.support_item_name || null}
                isRequired={false}
              />
            </div>
            <div className="ca-fields-row ca-fields-row--2">
              <div className="min-w-0">
                <label className="ca-field-label">NDIS Item Number</label>
                <div className="hidden">
                  <select
                    {...register("km_item_id", {
                      required: parseBooleanVal(watchFormData?.applykm),
                    })}
                    name="km_item_id"
                    className="hidden did-floating-input"
                    defaultValue={false}
                  />
                </div>
                <SelectOption
                  useMenuPortal={true}
                  data={traveldata || []}
                  selectedOption={travelNdisNum}
                  handleChange={(e) => {
                    setTravelNdisNum(e);
                    setValue("km_item_id", e?.value);
                  }}
                  loading={loading}
                  isDisabled={loading || billingDisabled}
                />
                {errors.km_item_id && (
                  <span className="text-sm text-red-500">
                    This field is required
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <label className="ca-field-label">NDIS Item Rate</label>
                <SelectOption
                  useMenuPortal={true}
                  data={NDISRateOptions}
                  selectedOption={ndisitemRate}
                  handleChange={(e) => {
                    setNdisItemRate(e);
                  }}
                  isDisabled={billingDisabled}
                />
              </div>
            </div>
            {ndisitemRate?.value === "alternate" && (
              <div className="min-w-0">
                <label className="ca-field-label">Alternate Rate</label>
                <InputField
                  isDisabled={billingDisabled}
                  cssClass="mt-0 h-40-important text-gray-700"
                  inputWidth="w-full"
                  backgroundColor="#F3F4F6"
                  type="number"
                  step="0.01"
                  min="0"
                  name="km_alternate_nids_rate_amount"
                  register={register}
                  setValue={setValue}
                  isRequired={false}
                />
              </div>
            )}
          </div>
        ) : (
          <p className="ca-loading-inline">Loading travel details…</p>
        )
      ) : null}
    </TimesheetCodeAllocationSectionCard>
  );
  
  return (
    <div className="timesheet-tab-content timesheet-code-allocation min-h-0">
      <TimesheetModeBanner shiftDetails={shiftDetails} lane="billing" />
      <form onSubmit={handleSubmit(onSubmit)} className="ca-form">
        <div className="ca-top-zone">
        <TimesheetCodeAllocationBillingInsight
          shiftDetails={shiftDetails}
          spSettings={spSettings}
          timeDisplayPreference={timeDisplayPreference}
          invoiceEstimate={invoiceEstimateDisplay}
          showInvoiceEstimate={false}
        />

        <TimesheetCodeAllocationBillingFlags>
          {!isV5 && (
            <TimesheetCodeAllocationRatioBadge ratio={shiftDetails?.ratio} />
          )}
          <TimesheetCodeAllocationTogglePill label="Non-billable">
            <Controller
              name="non_billable"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <ToggleSwitch {...field} isSmall disabled={billingDisabled} />
              )}
            />
          </TimesheetCodeAllocationTogglePill>
          <TimesheetCodeAllocationTogglePill label="Cancelled">
            <Controller
              name="cancelled"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <ToggleSwitch {...field} isSmall disabled={billingDisabled} />
              )}
            />
          </TimesheetCodeAllocationTogglePill>
          {!isV5 && sleepoverEligibleCategory && (
            <TimesheetCodeAllocationTogglePill label="Sleepover">
              <Controller
                name="sleepover"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <ToggleSwitch {...field} isSmall disabled={billingDisabled} />
                )}
              />
            </TimesheetCodeAllocationTogglePill>
          )}
        </TimesheetCodeAllocationBillingFlags>

        </div>

        {legacySplitShift && (
          <div className="ca-split-warning">
            <IoWarningOutline className="h-5 w-5 shrink-0" />
            <p>This is Split Shift</p>
          </div>
        )}

        <div
          className={`ca-workspace${
            showBreakdownPanel ? " ca-workspace--billing-focus" : ""
          }`}
        >
          <div
            className={`ca-column ca-column--left${
              showBreakdownPanel ? " ca-column--settings" : ""
            }`}
          >
            {showBreakdownPanel && (
              <TimesheetCodeAllocationSettingsColumnHeader />
            )}
            <TimesheetCodeAllocationSectionCard
              title="Service & line item"
              subtitle="Shift window for this allocation"
              className={showBreakdownPanel ? "ca-section-card--compact" : ""}
            >
              <div className="ca-time-block">
              <div className="ca-time-row">
                <div className="ca-time-field">
                  <label className="ca-field-label">
                    Start time ({startTime ? customFormatDate(startTime) : " "})
                  </label>
                  <Controller
                    name="codeStartTime"
                    control={control}
                    rules={{ required: "Start time is required" }}
                    render={({ field }) => (
                      <input
                        type="time"
                        {...field}
                        value={field.value || ""}
                        className={errors.codeStartTime ? "border-red-500" : ""}
                        disabled={!shiftTimeEditable}
                        onChange={(e) =>
                          handleCodeShiftTimeChange("codeStartTime", e.target.value)
                        }
                      />
                    )}
                  />
                  {errors.codeStartTime && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.codeStartTime.message}
                    </p>
                  )}
                </div>
                <span className="ca-time-to">to</span>
                <div className="ca-time-field">
                  <label className="ca-field-label">
                    End time ({endTime && customFormatDate(endTime)})
                  </label>
                  <Controller
                    name="codeEndTime"
                    control={control}
                    rules={{ required: "end time is required" }}
                    render={({ field }) => (
                      <input
                        type="time"
                        {...field}
                        value={field.value || ""}
                        className={errors.codeEndTime ? "border-red-500" : ""}
                        disabled={!shiftTimeEditable}
                        onChange={(e) =>
                          handleCodeShiftTimeChange("codeEndTime", e.target.value)
                        }
                      />
                    )}
                  />
                  {errors.codeEndTime && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.codeEndTime.message}
                    </p>
                  )}
                </div>
              </div>
              <TimesheetCodeAllocationShiftDurationPill
                duration={shiftDurationLabel}
                showRevert={
                  shiftTimeChanged &&
                  !shiftTimePendingWarning &&
                  shiftTimeEditable
                }
                onRevert={handleRevertShiftTime}
              />
              <TimesheetCodeAllocationShiftTimePendingBar
                visible={shiftTimePendingWarning}
                hasMultiSegmentSplits={hasMultiSegmentBillingSplits(
                  shiftDetails,
                  resolveBreakdownRowsForShiftEdit()
                )}
                onCancel={handleShiftTimePendingCancel}
                onApply={handleShiftTimePendingApply}
              />
              </div>

              {showServiceCategoryFields && !isV5 && (
                <div className="ca-fields-row">
                  <div className="min-w-0">
                    <label className="ca-field-label--dark">Service</label>
                    <SelectOption
                      isSearch={true}
                      data={serviceTypes}
                      useMenuPortal={true}
                      selectedOption={serviceCategory}
                      handleChange={(e) => {
                        setServiceCategory(e);
                        isFirstAnyValueChanged.current = false;
                      }}
                      isDisabled={
                        loading || billingDisabled
                      }
                      loading={loading}
                    />
                  </div>
                  <div className="ca-fields-row ca-fields-row--2">
                    <div className="min-w-0">
                      <label className="ca-field-label--dark">State</label>
                      <SelectOption
                        isSearch={false}
                        useMenuPortal={true}
                        data={catalogStates}
                        selectedOption={catalogState}
                        handleChange={(e) => {
                          setCatalogState(e);
                          isFirstAnyValueChanged.current = false;
                        }}
                        isDisabled={
                          loading || billingDisabled
                        }
                        loading={loading}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="ca-field-label--dark">
                        Category Item Name
                      </label>
                      <SelectOption
                        isSearch={false}
                        useMenuPortal={true}
                        data={CATEGORY_ITEM_NAME_AND_NOTES}
                        selectedOption={categoryItemName}
                        handleChange={(e) => {
                          setCategoryItemName(e);
                          isFirstAnyValueChanged.current = false;
                        }}
                        isDisabled={
                          loading ||
                          !isTimesheetServiceCatNonSTA ||
                          billingDisabled
                        }
                        loading={loading}
                      />
                    </div>
                  </div>
                </div>
              )}
              {showRateStrip && (
                  <TimesheetCodeAllocationRateStrip
                    catalogue={rateStripValues.catalogue}
                    billRate={rateStripValues.billRate}
                    appliedRate={rateStripValues.appliedRate}
                  />
                )}
            </TimesheetCodeAllocationSectionCard>

            {isV5 ? (
              <V5ParticipantTravelPanel
                shiftDetails={shiftDetails}
                rows={participantTravelRows}
                onChange={setParticipantTravelRows}
                isDisabled={billingDisabled}
              />
            ) : (
              // Billing travel for basic / basic-split — show even on invoicing CA
              // (hidePayrollFields must not hide Travel & KM).
              travelSection
            )}
          </div>

          <div className="ca-column ca-column--right ca-column--billing">
            <div className="ca-billing-panel">
            {!isV5 && !legacySplitShift && (
              <TimesheetCodeAllocationBreakdownToggleRow
                // hint="Billing uses scheduled/actual times from Tab 1"
                label="Do you want to break down this timesheet?"
              >
                <Controller
                  name="invoice_breakdown"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <ToggleSwitch
                      {...field}
                      isSmall
                      disabled={billingDisabled}
                      onChange={() => {
                        const nextValue = !field.value;
                        isFirstAnyValueChanged.current = false;
                        if (nextValue) {
                          isFirstRender.current = false;
                        }
                        field.onChange(nextValue);
                      }}
                    />
                  )}
                />
              </TimesheetCodeAllocationBreakdownToggleRow>
            )}

            {showBreakdownPanel && breakdownSegmentCount > 0 && (
              <TimesheetCodeAllocationBreakdownsHeader
                segmentCount={breakdownSegmentCount}
                durationLabel={breakdownDurationLabel}
                participantCount={billingParticipantCount}
              />
            )}

            {showSplitAccordion && splitShiftBreakdownData.length > 0 ? (
              renderParticipantGroupedBreakdowns(
                splitShiftBreakdownData,
                renderSplitAccordionItem
              )
            ) : !isV5 && watchFormData?.invoice_breakdown ? (
              breakdownLoader ? (
                <TimesheetCodeAllocationTabSkeleton />
              ) : watchFormData?.invoice_breakdown &&
                timesheetBreakdown?.length > 0 ? (
                renderParticipantGroupedBreakdowns(
                  timesheetBreakdown,
                  renderStandardAccordionItem
                )
              ) : null
            ) : !isV5 ? (
              shiftDetails?.participants?.length > 0 ? (
                <div className="ca-participant-groups">
                  <TimesheetCodeAllocationParticipantGroup
                    participantIndex={1}
                    totalParticipants={1}
                    initials={getParticipantInitials(
                      shiftDetails.participants[0]
                    )}
                    participantLabel={formatBillingParticipantNames(
                      shiftDetails.participants
                    )}
                    breakdownCount={1}
                    ratio={shiftDetails?.ratio}
                    defaultExpanded
                  >
                    <TimesheetCodeAllocationSectionCard>
                      <div className="ca-ndis-panel">
                        <NDISItem
                          key={`ndis-item-${
                            watchFormData?.invoice_breakdown
                              ? "breakdown"
                              : "normal"
                          }`}
                          itemNumber={itemNumber}
                          ndisItemName={ndisItemName}
                          setNDISItemName={setNDISItemName}
                          setValue={setValue}
                          register={register}
                          control={control}
                          errors={errors}
                          watchFormData={watchFormData}
                          isTimesheetServiceCatNonSTA={
                            isTimesheetServiceCatNonSTA
                          }
                          loading={loading}
                          shiftDetails={shiftDetails}
                          spSettings={spSettings}
                          careSector={spSettings?.care_sector}
                          clearErrors={clearErrors}
                          isDisabled={billingDisabled}
                          serviceCategory={serviceCategory}
                          isNonBillable={watchFormData?.non_billable}
                        />
                      </div>
                    </TimesheetCodeAllocationSectionCard>
                  </TimesheetCodeAllocationParticipantGroup>
                </div>
              ) : (
                <TimesheetCodeAllocationSectionCard>
                  <div className="ca-ndis-panel">
                    <NDISItem
                      key={`ndis-item-${
                        watchFormData?.invoice_breakdown ? "breakdown" : "normal"
                      }`}
                      itemNumber={itemNumber}
                      ndisItemName={ndisItemName}
                      setNDISItemName={setNDISItemName}
                      setValue={setValue}
                      register={register}
                      control={control}
                      errors={errors}
                      watchFormData={watchFormData}
                      isTimesheetServiceCatNonSTA={isTimesheetServiceCatNonSTA}
                      loading={loading}
                      shiftDetails={shiftDetails}
                      spSettings={spSettings}
                      careSector={spSettings?.care_sector}
                      clearErrors={clearErrors}
                      isDisabled={billingDisabled}
                      serviceCategory={serviceCategory}
                      isNonBillable={watchFormData?.non_billable}
                    />
                  </div>
                </TimesheetCodeAllocationSectionCard>
              )
            ) : null}
            </div>
          </div>
        </div>

        <TimesheetCodeAllocationSaveBar
          loading={submitLoader}
          disabled={billingDisabled}
          invoiceLocked={billingLocked}
          invoiceNumber={getBillingInvoiceNumber(shiftDetails)}
        />
      </form>

      <TimesheetCodeAllocationUpdateWarningModal
        visible={showUpdateWarning}
        loading={submitLoader}
        onCancel={handleCancelUpdate}
        onConfirm={handleConfirmUpdate}
      />
    </div>
  );
};

export default TimeSheetBreakDown;
